/**
 * Fleet Control Centre - Main Tab Component
 * Unified interface for fleet data from multiple API sources
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Database, Fuel, Package, Users, MapPin, RefreshCw, CheckCircle, XCircle, Clock, Settings, ChevronDown, Wrench } from 'lucide-react';
import FleetHubOverview, { type FleetHubOverviewProps } from '../components/fleetDataHub/FleetHubOverview';
import SystemHealthPage from '../components/systemHealth/SystemHealthPage';
import FuelAnalytics from '../components/fleetDataHub/FuelAnalytics';
import OrdersSection from '../components/fleetDataHub/OrdersSection';
import DriverCompliance from '../components/fleetDataHub/DriverCompliance';
import GPSTracking, { type TelematicsTotals } from '../components/fleetDataHub/GPSTracking';
import VehicleIdlePanel from '../components/fleetDataHub/VehicleIdlePanel';
import MaintenanceSection from '../components/fleetDataHub/MaintenanceSection';
import type { TimePeriod } from '../components/TimePeriodSelector';
import { authenticatedFetch } from '../utils/apiClient';
import { controlCentrePeriodRange } from '../utils/controlCentrePeriodRange';
import { Button } from '@/components/ui/button';
import { STORED_VIEW_STALE_RECHECK_SECONDS, preparingRetrySeconds } from '../utils/storedView';
import { refreshSession } from '../auth/cognitoAuth';
import { API_ENDPOINTS } from '../config/api';
import { useConfig } from '../contexts/ConfigContext';
import { classifyOrderStatus } from '../utils/orderStatusBuckets';
import {
  formatIngestSourceList,
  pickOrderSourceIds,
  pickTelematicsSourceIds,
  pickDriverSourceIds,
  pickFuelSourceIds,
  telematicsGpsLoadingMessage,
} from '../utils/ingestSourceLabels';
import {
  summarizeTelematicsAggregates,
  summarizeTelematicsVehicles,
  type TelematicsVehicleLike,
} from '../utils/telematicsAvailabilityScore';
import { countProlongedIdleVehicles } from '../utils/countProlongedIdleVehicles';
import {
  CONTROL_CENTRE_INGEST_REFRESH_EVENT,
  consumeControlCentreIngestRefresh,
} from '../utils/controlCentreIngestRefresh';
import {
  MAINTENANCE_REFRESH_WINDOWED_CHUNK_DAYS,
  MAINTENANCE_SCHEDULE_HISTORY_DAYS,
} from '../utils/maintenanceScheduleWindows';
import { useUseCase } from '../contexts/UseCaseContext';
import { useTab } from '../contexts/TabContext';
import { DEFAULT_HUB_TAB_ID, useVisibilityTruthRefresh } from '../hooks/useVisibilityTruthRefresh';
import { isSessionAdmin } from '../utils/pageAccess';
import { 
  AnalyticsOverview, 
  DashboardOverview, 
  FleetDataHubSection,
  JigsawData,
  OptifleetData,
  SupatrakData,
  QargoData,
  AquariusData
} from '../types/fleetDataHub';
import {
  MOCK_ANALYTICS_OVERVIEW,
  MOCK_DASHBOARD_OVERVIEW,
  MOCK_JIGSAW_DATA,
  MOCK_OPTIFLEET_DATA,
  MOCK_QARGO_DATA,
  MOCK_AQUARIUS_DATA,
  MOCK_ORDERS,
} from '../data/fleetControlCentreMockData';
import {
  londonDateString,
  londonLastMonthRange,
  londonMonthToDateRange,
} from '../utils/ordersDailyPresentation';
import { daysBetweenYmd } from '../lib/calc';
import { ordersDailySupport, ordersNotAvailableMessage } from '../utils/ordersDailySupport';
import { useOrdersDailyAvailability } from '../hooks/useOrdersDailyAvailability';
import OrdersComingSoon from '../components/fleetDataHub/OrdersComingSoon';

/** Fleet Alerts + Control Centre tab period shared via hub state. */
type AlertsPeriod =
  | 'today'
  | 'yesterday'
  | 'last-week'
  | 'month-to-date'
  | 'last-month'
  | 'last_30_days'
  | 'last_180_days';

// Get API base URL from environment or use relative paths
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

const standardiseVehicleReg = (s: string | null | undefined): string =>
  (s ?? '').replace(/\s+/g, '').trim() || '-';

async function fetchJson<T>(url: string, label: string): Promise<T> {
  const response = await authenticatedFetch(url);
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as { error?: string; message?: string };
    throw new Error(body.message ?? body.error ?? `${label} failed with HTTP ${response.status}`);
  }
  return response.json() as Promise<T>;
}

const wait = (milliseconds: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, milliseconds));

/** Poll live GPS while the Telematics tab is open (not on Overview). */
const TELEMATICS_POLL_INTERVAL_MS = 5 * 60_000;
/**
 * Min gap between automatic Driver Iceberg re-reads on browser alt-tab.
 * Returning to Control Centre always refreshes (ignores this gap). Within the
 * gap, visibilitychange is skipped so focus thrash does not re-hit Trino.
 */
const DRIVER_TRUTH_REFRESH_MIN_INTERVAL_MS = 60_000;
/**
 * Re-read the Driver tab while it is on screen. Matches the 15-minute
 * Samsara tachograph / Aquarius schedules (infra/serverless/template.yaml),
 * so a wall screen picks up each new import without a manual Refresh.
 */
const DRIVER_TRUTH_POLL_INTERVAL_MS = 15 * 60_000;
/**
 * How often the open Driver tab asks whether a scheduled fetch has landed
 * (drivers/data-current-as-of: feed health only, no data query). When the
 * time moves on, the tab re-reads at once instead of waiting for the 15-minute
 * poll, which runs on its own clock and could show data up to 15 minutes old.
 */
const DRIVER_DATA_CURRENT_CHECK_MS = 60_000;
/** Order volume chart on Orders uses a rolling 7-day context for Today/Yesterday. */
const ORDERS_VOLUME_CHART_DAYS = 7;

const sections = [
  { id: 'overview' as const, label: 'Control Centre', icon: Database },
  { id: 'orders' as const, label: 'Orders', icon: Package },
  { id: 'gps' as const, label: 'Telematics', icon: MapPin },
  { id: 'fuel' as const, label: 'Fuel', icon: Fuel },
  { id: 'drivers' as const, label: 'Driver', icon: Users },
  { id: 'maintenance' as const, label: 'Maintenance', icon: Wrench },
];

// API info for loading screen / Refresh All menu
// Use generic labels (no vendor names) in the UI
const API_SOURCE_CATALOG: Array<{ id: string; name: string; description: string }> = [
  { id: 'jigsaw', name: 'Fuel cards', description: 'Transactions' },
  { id: 'qargo', name: 'Orders & TMS', description: 'Data' },
  { id: 'palletways', name: 'Pallet network', description: 'Consignments & tracking' },
  { id: 'optifleet', name: 'Telematics (GPS)', description: 'Tracking' },
  { id: 'samsara', name: 'Telematics (GPS)', description: 'Live GPS' },
  { id: 'supatrak', name: 'Telematics & assets', description: 'Asset tracking' },
  { id: 'aquarius', name: 'Driver', description: 'Hours & compliance' },
];

/** Separate from Aquarius "Driver" — defects, VOR, faults (Maintenance tab / refresh). */
const MAINTENANCE_REFRESH_SOURCE = {
  id: 'maintenance',
  name: 'Maintenance',
  description: 'Schedule, defects, VOR & faults (90d)',
} as const;

/** Preferred Refresh dropdown order (Driver after telematics). */
const REFRESH_SOURCE_ORDER = [
  'jigsaw',
  'qargo',
  'palletways',
  'optifleet',
  'samsara',
  'supatrak',
  'aquarius',
];

/** Default refresh menu when org ingest sources are not yet known */
const DEFAULT_REFRESH_SOURCES = REFRESH_SOURCE_ORDER
  .map((id) => API_SOURCE_CATALOG.find((s) => s.id === id))
  .filter((s): s is (typeof API_SOURCE_CATALOG)[number] => !!s)
  .filter((s) => ['jigsaw', 'aquarius', 'qargo', 'optifleet', 'supatrak'].includes(s.id));

type RefreshStatus = {
  [key: string]: 'pending' | 'loading' | 'complete' | 'error';
};

/**
 * `view="system-health"` renders the System Health page body instead of the
 * Control Centre sections. It reuses this component's data loading so the
 * health cards read exactly the inputs the Control Centre used to give them;
 * there is no section switcher and no refresh/demo controls in that view.
 */
export default function FleetDataHub({ view = 'control-centre' }: { view?: 'control-centre' | 'system-health' } = {}) {
  const { useCase } = useUseCase();
  const { activeTab } = useTab();
  const { config } = useConfig();
  const serviceType = useCase === 'emergency' ? 'emergency_services' : 'logistics';

  // Manual ingest (routine / deep / per-source / maintenance) is an admin-only
  // function — everyone else relies on the scheduled backend refresh and the
  // permission-gated Data Import screen. See the "Refresh All" control below.
  const isAdmin = useMemo(() => isSessionAdmin(), []);

  const [activeSection, setActiveSection] = useState<FleetDataHubSection>('overview');
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshStatus, setRefreshStatus] = useState<RefreshStatus>({});
  const [refreshDetailMessage, setRefreshDetailMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingMessage, setLoadingMessage] = useState<string | null>(null);
  /** Bumped to invalidate in-flight Maintenance refresh after unmount / newer run. */
  const maintenanceRefreshGenRef = useRef(0);
  
  // Data states
  const [analyticsOverview, setAnalyticsOverview] = useState<AnalyticsOverview | null>(null);
  const [dashboardOverview] = useState<DashboardOverview | null>(null);
  const [jigsawData, setJigsawData] = useState<JigsawData | null>(null);
  const [optifleetData, setOptifleetData] = useState<OptifleetData | null>(null);
  const [supatrakData, setSupatrakData] = useState<SupatrakData | null>(null);
  const [samsaraData, setSamsaraData] = useState<OptifleetData | null>(null);
  const [configuredTelematicsProviders, setConfiguredTelematicsProviders] = useState<string[]>([]);
  const [enabledIngestSources, setEnabledIngestSources] = useState<string[]>([]);
  /** True once the org's enabled sources have been read (or failed to) — decides whether the orders list loads up front. */
  const [ingestSourcesLoaded, setIngestSourcesLoaded] = useState(false);
  /** Bumped when a hub read skips the orders list, so the on-demand load re-checks. */
  const [ordersListSkipKey, setOrdersListSkipKey] = useState(0);
  /** The orders list on screen (if any) is older than the latest hub read. */
  const ordersListNeedsLoadRef = useRef(true);
  /** When the latest orders list request started (either path) — a skipped read older than this changes nothing. */
  const ordersListRequestedAtRef = useRef(0);
  const [isTelematicsRefreshing, setIsTelematicsRefreshing] = useState(false);
  const [liveTelematicsDataCurrentAsOf, setLiveTelematicsDataCurrentAsOf] = useState<string | null>(null);
  const [qargoData, setQargoData] = useState<QargoData | null>(null);
  const [aquariusData, setAquariusData] = useState<AquariusData | null>(null);
  /** Bumped by each hub load (Refresh All, ingest settle) so drivers re-read with it. 0 = hub not loaded yet. */
  const [hubDriversReloadKey, setHubDriversReloadKey] = useState(0);
  const hubDriversSeq = useRef(0);
  const hubDriversTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Order stats from full export data (accurate cancelled/completed counts for alerts)
  const [orderStats, setOrderStats] = useState<{ total: number; cancelled: number; completed: number; active: number; pending: number } | null>(null);
  /** Full orders array for Fleet Alerts period filtering (today, yesterday, last 7/30/180 days) */
  const [ordersForAlerts, setOrdersForAlerts] = useState<any[]>([]);
  /** Orders from zeecore truth layer, mapped to the Order shape expected by OrdersSection */
  const [zeecoreOrderList, setZeecoreOrderList] = useState<any[]>([]);
  /** Fleet Alerts time period for Orders (and label for Fuel/Drivers); Telematics is live (/vehicles/live) */
  const [alertsPeriod, setAlertsPeriod] = useState<AlertsPeriod>('today');
  /** Days of hub history currently loaded from zeecore (expand on demand when alerts period widens). */
  const loadedHubWindowDaysRef = useRef(0);
  /** Coalesce hub widens so period chips don't stack parallel 5-endpoint fetches. */
  const hubWidenInFlightRef = useRef(false);
  const hubWidenPendingDaysRef = useRef(0);
  /** Telematics totals so Fleet Alerts match the Telematics tab (computed here so they show on overview without opening Telematics) */
  const [telematicsTotals, setTelematicsTotals] = useState<TelematicsTotals | null>(null);
  /** VIN → customerVehicleName for deduplication when merging Optifleet + Supatrak */
  const [vinToCustomerNameMap, setVinToCustomerNameMap] = useState<Map<string, string>>(new Map());
  /** True after VIN map fetch has completed (so we only set stable totals and avoid 110→98 jump) */
  const [vinMapLoadComplete, setVinMapLoadComplete] = useState(false);

  /** When true, overview and all sections (fuel, telematics, orders, drivers) show mock data for screenshots */
  const [useMockData, setUseMockData] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // Non-admins never see the gear; clear demo mode if role changes mid-session.
  useEffect(() => {
    if (!isAdmin && useMockData) setUseMockData(false);
    if (!isAdmin && showSettings) setShowSettings(false);
  }, [isAdmin, useMockData, showSettings]);

  /** When navigating from Control Centre alerts, which KPI to highlight on the target page */
  const [highlightKPI, setHighlightKPI] = useState<string | null>(null);
  const clearHighlightKPI = useCallback(() => setHighlightKPI(null), []);
  /**
   * Bumped to re-read Driver Iceberg summaries when Control Centre becomes
   * visible again (app-tab keep-alive) or the browser tab regains focus.
   * Maintenance visibility refresh lives in MaintenanceSection (Health vs
   * Schedule have different costs / cooldowns).
   */
  const driverTruth = useVisibilityTruthRefresh({
    enabled: !useMockData && activeSection === 'drivers',
    activeTab,
    minIntervalMs: DRIVER_TRUTH_REFRESH_MIN_INTERVAL_MS,
  });
  // Poll the Driver tab every 15 minutes while it is open and visible. Hidden
  // browser tabs and other Control Centre sections skip the tick; returning
  // is already covered by the visibility refresh above. bump() shares that
  // hook's cooldown, so a manual or visibility re-read just before a tick is
  // not repeated.
  const bumpDriverTruth = driverTruth.bump;
  useEffect(() => {
    if (useMockData || activeSection !== 'drivers' || activeTab !== DEFAULT_HUB_TAB_ID) return;
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      bumpDriverTruth();
    }, DRIVER_TRUTH_POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [useMockData, activeSection, activeTab, bumpDriverTruth]);
  // Re-read as soon as a new fetch lands. The first answer only sets the
  // baseline (the tab has just loaded), even when it is null (a fetch still
  // settling): the first time after that is then a change, not a new
  // baseline. A failed check is skipped; the 15-minute poll still covers it.
  useEffect(() => {
    if (useMockData || activeSection !== 'drivers' || activeTab !== DEFAULT_HUB_TAB_ID) return;
    let lastSeen: string | null | undefined;
    let cancelled = false;
    const check = async () => {
      if (document.visibilityState === 'hidden') return;
      try {
        const res = await authenticatedFetch(API_ENDPOINTS.fleetIntelligence.driversDataCurrentAsOf());
        if (!res.ok || cancelled) return;
        const { data_current_as_of: answer } = (await res.json()) as { data_current_as_of?: string | null };
        if (cancelled) return;
        const current = answer ?? null;
        if (lastSeen !== undefined && current !== null && current !== lastSeen) bumpDriverTruth();
        // A null (settling) answer never replaces a known time.
        if (lastSeen === undefined || current !== null) lastSeen = current;
      } catch {
        /* next check, or the 15-minute poll */
      }
    };
    void check();
    const intervalId = window.setInterval(() => void check(), DRIVER_DATA_CURRENT_CHECK_MS);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [useMockData, activeSection, activeTab, bumpDriverTruth]);
  /** Bumped only after Maintenance-only vendor ingest — forces Health + Schedule re-read. */
  const [maintenanceIngestRefreshKey, setMaintenanceIngestRefreshKey] = useState(0);
  // Driver remount on section enter already fetches; hub-return / tab-focus
  // force-refresh is handled inside useVisibilityTruthRefresh — do not also
  // bump on section enter (that double-fetched Iceberg).
  const settingsRef = useRef<HTMLDivElement>(null);
  const refreshDropdownRef = useRef<HTMLDivElement>(null);
  /** Section switcher — sticky under the app chrome while scrolling long tabs. */
  const sectionNavRef = useRef<HTMLDivElement>(null);
  const skipSectionScrollRef = useRef(true);
  const telematicsFetchInFlightRef = useRef(false);
  const lastTelematicsPollAtRef = useRef(0);
  const [showRefreshDropdown, setShowRefreshDropdown] = useState(false);

  // Close settings dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target as Node)) {
        setShowSettings(false);
      }
      if (refreshDropdownRef.current && !refreshDropdownRef.current.contains(event.target as Node)) {
        setShowRefreshDropdown(false);
      }
    };
    
    if (showSettings || showRefreshDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSettings, showRefreshDropdown]);

  // After a section switch (nav click or alert jump), bring the switcher to the
  // top of the scroll area so the new tab does not open mid-page.
  useEffect(() => {
    if (skipSectionScrollRef.current) {
      skipSectionScrollRef.current = false;
      return;
    }
    sectionNavRef.current?.scrollIntoView({ block: 'start', behavior: 'auto' });
  }, [activeSection]);

  const apiInfo = useMemo(() => {
    const matched =
      enabledIngestSources.length === 0
        ? DEFAULT_REFRESH_SOURCES
        : API_SOURCE_CATALOG.filter((s) => enabledIngestSources.includes(s.id));
    const list = matched.length > 0 ? matched : DEFAULT_REFRESH_SOURCES;
    return [...list].sort((a, b) => {
      const ai = REFRESH_SOURCE_ORDER.indexOf(a.id);
      const bi = REFRESH_SOURCE_ORDER.indexOf(b.id);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });
  }, [enabledIngestSources]);

  const orderIngestSources = useMemo(() => {
    const fromConfig = pickOrderSourceIds(enabledIngestSources);
    const fromList = new Set<string>();
    for (const row of zeecoreOrderList) {
      const src = String(row?.source || '').toLowerCase();
      if (src === 'same_day' || src === 'declared') fromList.add('same_day');
      else if (src === 'qargo' || src === 'palletways') fromList.add(src);
    }
    return [...new Set([...fromConfig, ...fromList])];
  }, [enabledIngestSources, zeecoreOrderList]);

  /**
   * Palletways-only orgs: the Overview's order figures come from the stored
   * orders_daily snapshot + series, so the slow orders list is only needed
   * once the Orders section is opened. Any other org (or unknown sources)
   * keeps loading it with the hub, since its Overview counts use it.
   * Same rule as `ordersDailyEnabled`, from config only (not from the list).
   */
  // Which orders view this org gets is the server's decision (GET /orders/v1/availability).
  const { availability: ordersAvailability } = useOrdersDailyAvailability({ enabled: !useMockData });
  const ordersSupport = useMockData ? 'available' : ordersDailySupport(ordersAvailability);
  const ordersListWanted = useMemo(() => {
    if (useMockData || !ingestSourcesLoaded || ordersSupport === 'loading') return false;
    const ordersListOnDemand = ordersSupport === 'available';
    return !ordersListOnDemand || activeSection === 'orders';
  }, [useMockData, ingestSourcesLoaded, ordersSupport, activeSection]);
  const ordersListWantedRef = useRef(ordersListWanted);
  ordersListWantedRef.current = ordersListWanted;

  const telematicsProviderIds = useMemo(() => {
    const fromLive = pickTelematicsSourceIds(configuredTelematicsProviders);
    if (fromLive.length > 0) return fromLive;
    return pickTelematicsSourceIds(enabledIngestSources);
  }, [configuredTelematicsProviders, enabledIngestSources]);

  const telematicsLoadingMsg = useMemo(
    () => telematicsGpsLoadingMessage(telematicsProviderIds),
    [telematicsProviderIds],
  );

  const orderSourceLabel = useMemo(
    () => formatIngestSourceList(orderIngestSources) || 'your order systems',
    [orderIngestSources],
  );

  /** Board, "not available yet", or the older view — as the server decided (utils/ordersDailySupport.ts). */
  const ordersDailyEnabled = ordersSupport === 'available';
  const ordersUnavailableMessage = useMockData ? undefined : ordersNotAvailableMessage(ordersAvailability);

  const driverIngestSources = useMemo(
    () => pickDriverSourceIds(enabledIngestSources),
    [enabledIngestSources],
  );

  const fuelIngestSources = useMemo(
    () => pickFuelSourceIds(enabledIngestSources),
    [enabledIngestSources],
  );

  const driverSourceLabel = useMemo(
    () => formatIngestSourceList(driverIngestSources) || 'your driver systems',
    [driverIngestSources],
  );

  const fuelSourceLabel = useMemo(
    () => formatIngestSourceList(fuelIngestSources) || 'your fuel systems',
    [fuelIngestSources],
  );

  const fetchEnabledIngestSources = useCallback(async () => {
    try {
      const res = await authenticatedFetch(API_ENDPOINTS.ingestionManage.status());
      if (!res.ok) return;
      const json = await res.json().catch(() => ({})) as { enabledSources?: string[] };
      if (Array.isArray(json.enabledSources)) {
        setEnabledIngestSources(json.enabledSources);
      }
    } catch (err) {
      console.warn('[FleetDataHub] Failed to load enabled ingest sources:', err);
    } finally {
      setIngestSourcesLoaded(true);
    }
  }, []);

  /** Run ZeeCore incremental ingest (optionally scoped) and wait until the job completes. */
  const runIncrementalImport = useCallback(async (
    sources?: string[],
    options?: {
      deepRefresh?: boolean;
      /** Samsara stream filter — used when Driver refresh pulls tachograph hours. */
      samsaraScope?: 'telematics' | 'tachograph';
    },
  ) => {
    const res = await authenticatedFetch(API_ENDPOINTS.ingestionManage.incremental(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...(sources?.length ? { sources } : {}),
        ...(options?.deepRefresh ? { deepRefresh: true } : {}),
        ...(options?.samsaraScope ? { samsaraScope: options.samsaraScope } : {}),
      }),
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({})) as { message?: string; error?: string };
      throw new Error(data.message ?? data.error ?? `HTTP ${res.status}`);
    }

    const startedJob = await res.json() as { jobId?: string; status?: string; message?: string };
    if (!startedJob.jobId) {
      throw new Error(startedJob.message || 'Incremental import did not return a job ID');
    }

    console.log(
      '[Refresh] Incremental import started:',
      startedJob.jobId,
      sources?.length ? sources : 'all enabled',
      options?.deepRefresh ? '(deep)' : '(routine)',
      options?.samsaraScope ? `(samsara:${options.samsaraScope})` : '',
    );

    const pollDeadline = Date.now() + 10 * 60 * 1000;
    while (Date.now() < pollDeadline) {
      const job = await fetchJson<{
        status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
        error?: string;
      }>(
        API_ENDPOINTS.ingestionManage.jobStatus(startedJob.jobId),
        'Incremental import status',
      );
      if (job.status === 'completed') return;
      if (job.status === 'failed' || job.status === 'cancelled') {
        throw new Error(job.error ?? `Incremental import ${job.status}`);
      }
      await wait(3000);
    }

    throw new Error('Incremental import did not complete within 10 minutes');
  }, []);

  /**
   * Direct Palletways pull when orchestrator watermarks are missing (pre-backfill pilots).
   * Routine: last 2 days; deep: last 7 days — consignments + tracking notes for KPIs.
   */
  const runPalletwaysDirectIngest = useCallback(async (options?: { deepRefresh?: boolean }) => {
    const lookbackDays = options?.deepRefresh ? 7 : 2;
    const now = new Date();
    const endStr = now.toISOString().split('T')[0];
    const start = new Date(now);
    start.setUTCDate(start.getUTCDate() - lookbackDays);
    const startStr = start.toISOString().split('T')[0];
    const base = API_ENDPOINTS.base || '';

    // Backfill pulls can legitimately run longer than the default request timeout.
    const INGEST_TIMEOUT_MS = 90_000;

    const consignmentsRes = await authenticatedFetch(`${base}/ingest/palletways/consignments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        startDate: startStr,
        endDate: endStr,
        // Routine: skip getconsignment enrichment (addresses already backfilled).
        enrichDetails: options?.deepRefresh === true,
      }),
    }, INGEST_TIMEOUT_MS);
    if (!consignmentsRes.ok) {
      const data = await consignmentsRes.json().catch(() => ({})) as { message?: string; error?: string };
      throw new Error(data.message ?? data.error ?? `Palletways consignments failed (${consignmentsRes.status})`);
    }

    // Date-range notes (not delta): needed to mark entered consignments completed via DAR.
    const notesRes = await authenticatedFetch(`${base}/ingest/palletways/tracking-notes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ startDate: startStr, endDate: endStr }),
    }, INGEST_TIMEOUT_MS);
    if (!notesRes.ok) {
      const data = await notesRes.json().catch(() => ({})) as { message?: string; error?: string };
      console.warn('[Refresh] Palletways tracking-notes failed:', data.message ?? data.error ?? notesRes.status);
    }
  }, []);

  // Load VIN → customerVehicleName for telematics merge/dedupe (same as GPSTracking)
  useEffect(() => {
    setVinMapLoadComplete(false);
    let isCancelled = false;
    authenticatedFetch(API_ENDPOINTS.optifleetVehicleList(serviceType))
      .then((res) => res.json().catch(() => ({})))
      .then((json: { ok?: boolean; rows?: Array<{ vin?: string; customerVehicleName?: string }> }) => {
        if (isCancelled || !json?.ok || !Array.isArray(json.rows)) return;
        const mapping = new Map<string, string>();
        json.rows.forEach((row) => {
          const vin = (row.vin ?? '').trim();
          const customerName = (row.customerVehicleName ?? '').trim();
          const displayName = standardiseVehicleReg(customerName);
          if (vin && displayName && displayName !== '-') {
            const normalizedVin = vin.replace(/\s+/g, '');
            mapping.set(normalizedVin, displayName);
            mapping.set(normalizedVin.toUpperCase(), displayName);
            mapping.set(normalizedVin.toLowerCase(), displayName);
            mapping.set(vin, displayName);
          }
        });
        setVinToCustomerNameMap(mapping);
      })
      .catch(() => {})
      .finally(() => {
        if (!isCancelled) setVinMapLoadComplete(true);
      });
    return () => { isCancelled = true; };
  }, [serviceType]);

  // Compute telematics totals for Fleet Alerts + Availability Score (same inputs as GPSTracking).
  const computedTelematicsTotals = useMemo((): TelematicsTotals | null => {
    type ProviderSlice = {
      vehicles?: Array<{
        id?: string;
        status?: string;
        lastUpdate?: string | null;
        LocalTime?: string | null;
        latitude?: number | null;
        longitude?: number | null;
        lat?: number | null;
        lng?: number | null;
        prolonged_idle?: boolean;
      }>;
      vehicle_count?: number;
      alert_count?: number;
      idle_vehicles?: number;
      prolonged_idle_vehicles?: number;
      moving_vehicles?: number;
      stopped_vehicles?: number;
      offline_vehicles?: number;
      position_count?: number;
    } | null;

    const toTotals = (
      summary: ReturnType<typeof summarizeTelematicsVehicles>,
      totalVehicles: number,
      alertCount: number,
      positionCount: number,
      prolongedIdleVehicles: number,
    ): TelematicsTotals => ({
      totalVehicles,
      alertCount,
      idleVehicles: summary.idleVehicles,
      prolongedIdleVehicles,
      movingVehicles: summary.movingVehicles,
      stoppedVehicles: summary.stoppedVehicles,
      offlineVehicles: summary.offlineVehicles,
      vehiclesWithGps: summary.vehiclesWithGps,
      positionCount,
      availabilityScore: summary.availabilityScore,
      scorable: summary.scorable,
    });

    const totalsFromAggregates = (d: NonNullable<ProviderSlice>): TelematicsTotals => {
      const alertCount = d.alert_count ?? 0;
      const positionCount = d.position_count ?? 0;
      const prolongedIdleVehicles = countProlongedIdleVehicles(d);
      if (Array.isArray(d.vehicles) && d.vehicles.length > 0) {
        const summary = summarizeTelematicsVehicles(d.vehicles, { alertCount, positionCount });
        return toTotals(summary, d.vehicles.length || d.vehicle_count || 0, alertCount, positionCount, prolongedIdleVehicles);
      }
      const summary = summarizeTelematicsAggregates({
        totalVehicles: d.vehicle_count,
        movingVehicles: d.moving_vehicles,
        idleVehicles: d.idle_vehicles,
        stoppedVehicles: d.stopped_vehicles,
        offlineVehicles: d.offline_vehicles,
        alertCount,
        positionCount,
      });
      return toTotals(summary, summary.totalVehicles, alertCount, positionCount, prolongedIdleVehicles);
    };

    const opt = optifleetData as ProviderSlice;
    const sup = supatrakData as ProviderSlice;
    const sam = samsaraData as ProviderSlice;

    if (!opt && !sup && !sam) return null;

    if (sam && !opt && !sup) return totalsFromAggregates(sam);
    if (opt && !sup && !sam) return totalsFromAggregates(opt);
    if (!opt && sup && !sam) return totalsFromAggregates(sup);

    if (opt && sup && !sam) {
      if (!vinMapLoadComplete) return null;
      const norm = (id: string) => standardiseVehicleReg(id) || (id ?? '').trim() || id;
      const getMergeKey = (v: { id?: string }, source: 'optifleet' | 'supatrak'): string => {
        const rawId = v?.id ?? '';
        const n = norm(rawId);
        if (source === 'optifleet') {
          const customerName = vinToCustomerNameMap.get(n) ?? vinToCustomerNameMap.get(n.toUpperCase()) ?? vinToCustomerNameMap.get(n.toLowerCase()) ?? vinToCustomerNameMap.get(rawId.trim());
          return norm(customerName ?? rawId);
        }
        return norm(rawId);
      };
      const vehicleMap = new Map<string, TelematicsVehicleLike & { id?: string }>();
      opt.vehicles?.forEach((v) => vehicleMap.set(getMergeKey(v, 'optifleet'), v));
      sup.vehicles?.forEach((v) => vehicleMap.set(getMergeKey(v, 'supatrak'), v));
      const mergedVehicles = [...vehicleMap.values()];
      const alertCount = (opt.alert_count ?? 0) + (sup.alert_count ?? 0);
      const positionCount = (opt.position_count ?? 0) + (sup.position_count ?? 0);
      const summary = summarizeTelematicsVehicles(mergedVehicles, { alertCount, positionCount });
      return toTotals(
        summary,
        mergedVehicles.length,
        alertCount,
        positionCount,
        countProlongedIdleVehicles({ vehicles: mergedVehicles as Array<{ prolonged_idle?: boolean }> }),
      );
    }

    if (configuredTelematicsProviders.length > 0) {
      for (const provider of configuredTelematicsProviders) {
        if (provider === 'samsara' && sam) return totalsFromAggregates(sam);
        if (provider === 'optifleet' && opt) return totalsFromAggregates(opt);
        if (provider === 'supatrak' && sup) return totalsFromAggregates(sup);
      }
    }

    if (sam) return totalsFromAggregates(sam);
    if (opt) return totalsFromAggregates(opt);
    if (sup) return totalsFromAggregates(sup);
    return null;
  }, [optifleetData, supatrakData, samsaraData, configuredTelematicsProviders, vinToCustomerNameMap, vinMapLoadComplete]);

  // Push computed totals into state so Fleet Alerts and GPSTracking (when open) can use them. GPSTracking still reports when open and overwrites; computed ensures overview has numbers before user opens Telematics.
  useEffect(() => {
    if (computedTelematicsTotals != null) setTelematicsTotals(computedTelematicsTotals);
  }, [computedTelematicsTotals]);

  // Fetch specific API data
  const fetchApiData = useCallback(async (api: 'jigsaw' | 'optifleet' | 'supatrak' | 'qargo' | 'aquarius') => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/api/analytics/${api}`);
      if (!response.ok) throw new Error(`Failed to fetch ${api} data`);
      
      const data = await response.json();
      switch (api) {
        case 'jigsaw':
          if (data.data) setJigsawData(data.data);
          break;
        case 'optifleet':
          // Use real-time API endpoint for live data with SSE streaming for progress
          console.log('[FleetDataHub] Fetching Optifleet realtime data with SSE streaming...');
          // Set initial loading message immediately when fetch starts
          setLoadingMessage(telematicsLoadingMsg);
          try {
            // Use EventSource for SSE streaming to show pagination progress
            // IMPORTANT: Keep isLoading=true throughout the entire SSE connection
            // Only set it to false when we receive 'complete' or a real error
            // Legacy Optifleet SSE path (live Control Centre uses /vehicles/live).
            const tokens = await refreshSession();
            const token = tokens?.idToken;
            // Legacy manual-refresh path only — live Control Centre polling (/vehicles/live)
            // is a fixed 5-minute poll and does not use this setting.
            const optifleetInterval = config.telematics.latestIntervalDisplay || '24h';
            const baseUrl = `${API_ENDPOINTS.optifleetData(true)}?stream=true&interval=${encodeURIComponent(optifleetInterval)}`;
            const url = token ? `${baseUrl}&token=${encodeURIComponent(token)}` : baseUrl;
            await new Promise<void>((resolve, reject) => {
              const eventSource = new EventSource(url);
              let finalData: any = null;
              let isResolved = false; // Track if Promise has been resolved/rejected
              
              eventSource.addEventListener('start', (e) => {
                const data = JSON.parse(e.data);
                console.log('[FleetDataHub] SSE start:', data);
                // Message already set above, but update if needed
                if (!loadingMessage) {
                  setLoadingMessage(telematicsLoadingMsg);
                }
                // Keep loading state active - don't change isLoading here
              });
              
              eventSource.addEventListener('progress', (e) => {
                const data = JSON.parse(e.data);
                console.log('[FleetDataHub] SSE progress:', data);
                // Update loading message with pagination progress
                const page = data.page || 0;
                const totalRecords = data.totalRecords || 0;
                if (page > 0) {
                  setLoadingMessage(`${telematicsLoadingMsg} (Page ${page}, ${totalRecords} records)`);
                } else {
                  setLoadingMessage(telematicsLoadingMsg);
                }
                // Keep loading state active during pagination - don't change isLoading here
                // This is where pagination happens, so we must keep isLoading=true
              });
              
              eventSource.addEventListener('complete', (e) => {
                if (isResolved) return; // Prevent multiple calls
                isResolved = true;
                
                const response = JSON.parse(e.data);
                console.log('[FleetDataHub] SSE complete:', response);
                finalData = response;
                eventSource.close();
                
                if (response.ok && response.data) {
                  const vehiclesArray = response.data.vehicles !== undefined 
                    ? (Array.isArray(response.data.vehicles) ? response.data.vehicles : [])
                    : [];
                  
                  const dataWithExport = {
                    ...response.data,
                    vehicles: vehiclesArray,
                    ...(response.export_file && { export_file: response.export_file }),
                    ...(response.data.export_timestamp && { export_timestamp: response.data.export_timestamp })
                  };
                  
                  if (!('vehicles' in dataWithExport)) {
                    console.error('[FleetDataHub] CRITICAL: vehicles key missing after spread! Adding it now.');
                    dataWithExport.vehicles = [];
                  }
                  
                  setOptifleetData(dataWithExport);
                  setIsLoading(false); // Only set to false when complete
                  setLoadingMessage(null); // Clear loading message when complete
                  // Clear any previous errors on success
                  setError(null);
                  resolve();
                } else {
                  // If response is not successful, log but don't set persistent error
                  // This allows the UI to continue functioning
                  console.warn('[FleetDataHub] Optifleet response not successful:', response);
                  setIsLoading(false);
                  setLoadingMessage(null);
                  // Don't set error state - allow retry on next fetch
                  reject(new Error('Failed to fetch Optifleet data'));
                }
              });
              
              eventSource.addEventListener('error', (e: any) => {
                // Don't treat SSE error events as failures if we already have data
                // SSE error events can fire for various reasons but 'complete' handler handles success
                if (isResolved) return; // Already handled
                
                // Only log, don't set error state - let the complete handler handle it
                const errorData = e.data ? JSON.parse(e.data) : { error: 'Unknown error' };
                console.warn('[FleetDataHub] SSE error event (may be non-fatal):', errorData);
                
                // Only treat as fatal error if connection is actually closed and we have no data
                if (eventSource.readyState === EventSource.CLOSED && !finalData && !isResolved) {
                  console.error('[FleetDataHub] SSE connection closed before receiving data');
                  isResolved = true;
                  eventSource.close();
                  setIsLoading(false);
                  setLoadingMessage(null);
                  // Don't set error state - let it retry on next fetch
                  reject(new Error('Connection closed before data was received'));
                }
              });
              
              // Handle connection errors - only handle actual connection failures
              // EventSource.onerror fires for various reasons, not just errors
              // We should only treat it as an error if the connection is closed AND we haven't received final data
              eventSource.onerror = (error) => {
                // Only handle as error if connection is closed and we haven't received final data
                if (eventSource.readyState === EventSource.CLOSED && !finalData && !isResolved) {
                  console.error('[FleetDataHub] EventSource connection closed unexpectedly:', error);
                  isResolved = true;
                  eventSource.close();
                  setIsLoading(false);
                  setLoadingMessage(null);
                  // Don't set error state - connection errors are often transient
                  reject(new Error('Connection closed before data was received'));
                }
                // For other states (CONNECTING, OPEN), ignore - these are normal during streaming
                // The connection might be establishing or streaming, which is fine
              };
            });
          } catch (error) {
            console.error('[FleetDataHub] Error setting up SSE for Optifleet data:', error);
            setIsLoading(false);
            setLoadingMessage(null); // Clear message on error
            // Don't set persistent error - setup errors are often transient
            // Fallback to old endpoint data if available
            if (data.data) {
              console.log('[FleetDataHub] Using fallback data due to SSE error');
              const fallbackData = {
                ...data.data,
                vehicles: Array.isArray(data.data.vehicles) ? data.data.vehicles : []
              };
              setOptifleetData(fallbackData);
            }
          }
          // IMPORTANT: Don't let the finally block reset isLoading for optifleet case
          // We handle isLoading state explicitly in the Promise handlers above
          // Skip the finally block's setIsLoading(false) for this case
          return; // Return early to prevent finally block from running
          break;
        case 'supatrak':
          // Use real-time API endpoint similar to Optifleet
          console.log('[FleetDataHub] Fetching Supatrak realtime data...');
          setLoadingMessage(telematicsLoadingMsg);
          // Clear any previous errors when starting a new fetch
          setError(null);
          try {
            const supatrakResponse = await authenticatedFetch(API_ENDPOINTS.supatrakData(true));
            if (supatrakResponse.ok) {
              const supatrakResult = await supatrakResponse.json();
              if (supatrakResult.ok && supatrakResult.data) {
                const vehiclesArray = supatrakResult.data.vehicles !== undefined 
                  ? (Array.isArray(supatrakResult.data.vehicles) ? supatrakResult.data.vehicles : [])
                  : [];
                
                const dataWithExport = {
                  ...supatrakResult.data,
                  vehicles: vehiclesArray,
                  ...(supatrakResult.export_file && { export_file: supatrakResult.export_file }),
                  ...(supatrakResult.data.export_timestamp && { export_timestamp: supatrakResult.data.export_timestamp })
                };
                
                if (!('vehicles' in dataWithExport)) {
                  console.error('[FleetDataHub] CRITICAL: vehicles key missing for Supatrak! Adding it now.');
                  dataWithExport.vehicles = [];
                }
                
                setSupatrakData(dataWithExport);
              }
            }
          } catch (error) {
            console.error('[FleetDataHub] Error fetching Supatrak data:', error);
          }
          break;
        case 'qargo':
          if (data.data) setQargoData(data.data);
          break;
        case 'aquarius':
          // Use the dedicated Aquarius endpoint (processed through analytics engine)
          try {
            const aquaResponse = await authenticatedFetch(API_ENDPOINTS.aquariusData());
            if (aquaResponse.ok) {
              const aquaData = await aquaResponse.json();
              console.log('[FleetDataHub] Aquarius response:', aquaData);
              if (aquaData.ok && aquaData.data) {
                console.log('[FleetDataHub] Setting Aquarius data, driver count:', aquaData.data.driver_count);
                console.log('[FleetDataHub] Driver details:', aquaData.data.driver_details?.length || 0);
                setAquariusData(aquaData.data);
              } else {
                console.warn('[FleetDataHub] Aquarius endpoint returned unsuccessful or no data:', aquaData);
                // Fallback to analytics endpoint data if available
                if (data.data) {
                  console.log('[FleetDataHub] Using fallback data from analytics endpoint');
                  setAquariusData(data.data);
                }
              }
            } else {
              console.error('[FleetDataHub] Aquarius endpoint not OK:', aquaResponse.status);
              // Fallback to analytics endpoint data if available
              if (data.data) {
                console.log('[FleetDataHub] Using fallback data due to failed response');
                setAquariusData(data.data);
              }
            }
          } catch (error) {
            console.error('[FleetDataHub] Error fetching Aquarius data:', error);
            // Fallback to analytics endpoint data if available
            if (data.data) {
              console.log('[FleetDataHub] Using fallback data due to exception');
              setAquariusData(data.data);
            }
          }
          break;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to fetch ${api} data`);
    } finally {
      // Only set loading to false if it's not the optifleet case
      // (optifleet handles its own loading state via SSE events)
      if (api !== 'optifleet') {
        setIsLoading(false);
      }
    }
  }, [telematicsLoadingMsg, config.telematics.latestIntervalDisplay]);

  // Direct fetch for Supatrak data — bypasses the /api/analytics/supatrak wrapper
  // which is unavailable in this environment. Calls the old server's reliable endpoint
  // that has its own fallback chain (live API → S3 exports).
  const fetchSupatrakDirect = useCallback(async () => {
    try {
      const res = await authenticatedFetch(API_ENDPOINTS.supatrakData(true));
      if (!res.ok) return;
      const result = await res.json();
      if (result.ok && result.data) {
        const vehiclesArray = Array.isArray(result.data.vehicles) ? result.data.vehicles : [];
        setSupatrakData({
          ...result.data,
          vehicles: vehiclesArray,
          ...(result.export_file && { export_file: result.export_file }),
          ...(result.data.export_timestamp && { export_timestamp: result.data.export_timestamp }),
        });
      }
    } catch (e) {
      console.warn('[FleetDataHub] Supatrak direct fetch failed:', e);
    }
  }, []);

  // Fetch order stats from the full export data (for accurate alert counts)
  const fetchOrderStats = useCallback(async () => {
    try {
      const res = await authenticatedFetch(`${API_BASE_URL}/api/qargo/orders`);
      if (!res.ok) return;
      const data = await res.json();
      const orders = data.orders || [];
      setOrdersForAlerts(Array.isArray(orders) ? orders : []);
      if (orders.length === 0) return;
      
      const stats = { total: orders.length, cancelled: 0, completed: 0, active: 0, pending: 0 };
      orders.forEach((o: any) => {
        switch (classifyOrderStatus(o.status)) {
          case 'cancelled':
            stats.cancelled++;
            break;
          case 'completed':
            stats.completed++;
            break;
          case 'in_progress':
            stats.active++;
            break;
          default:
            // entered / booked / pending → Active open pipeline
            stats.active++;
            stats.pending++;
            break;
        }
      });
      console.log('[FleetDataHub] Order stats from full export:', stats);
      setOrderStats(stats);
    } catch (e) {
      console.warn('[FleetDataHub] Could not fetch order stats:', e);
    }
  }, []);

  // Date range for Fleet Alerts period — the same windows as the Driver tab's
  // chips (London calendar month for month chips; UTC days for the rest), so
  // the hub's driver figures come from zeecore's stored Driver tab periods.
  const alertsPeriodDateRange = useMemo(() => controlCentrePeriodRange(alertsPeriod), [alertsPeriod]);

  // Hub driver figures (Fleet Alerts, overview) for the alerts period, read
  // from the stored Driver tab view: no Athena on a page load. 202 = the
  // period is being prepared (first view, or just after midnight) — ask
  // again when ready; stale figures are shown and re-read after the refresh.
  useEffect(() => {
    if (useMockData || hubDriversReloadKey === 0) return;
    const seq = ++hubDriversSeq.current;
    const clearTimer = () => {
      if (hubDriversTimer.current) clearTimeout(hubDriversTimer.current);
      hubDriversTimer.current = null;
    };
    clearTimer();
    const load = async (): Promise<void> => {
      try {
        const res = await authenticatedFetch(API_ENDPOINTS.fleetIntelligence.driversSummary({
          from: alertsPeriodDateRange.start.toISOString(),
          to: alertsPeriodDateRange.end.toISOString(),
        }));
        if (seq !== hubDriversSeq.current) return;
        if (res.status === 202) {
          const waitSeconds = await preparingRetrySeconds(res);
          if (seq === hubDriversSeq.current) hubDriversTimer.current = setTimeout(() => { void load(); }, waitSeconds * 1000);
          return;
        }
        if (!res.ok) throw new Error(`Driver summary failed with HTTP ${res.status}`);
        const json = (await res.json()) as AquariusData;
        if (seq !== hubDriversSeq.current) return;
        setAquariusData(json);
        if (json.projection?.stale_reason) {
          hubDriversTimer.current = setTimeout(() => { void load(); }, STORED_VIEW_STALE_RECHECK_SECONDS * 1000);
        }
      } catch (err) {
        console.warn('[FleetDataHub] Driver summary for the alerts period failed:', err);
      }
    };
    void load();
    return clearTimer;
  }, [useMockData, hubDriversReloadKey, alertsPeriodDateRange]);

  // Period-filtered order stats for Fleet Alerts + System Health (same deduped list as Orders tab)
  const periodFilteredOrderStats = useMemo((): { total: number; cancelled: number; completed: number; active: number; pending: number } | null => {
    const sourceList = zeecoreOrderList.length > 0 ? zeecoreOrderList : ordersForAlerts;
    if (sourceList.length === 0) return orderStats;
    const { start, end } = alertsPeriodDateRange;
    const filtered: any[] = [];
    for (const o of sourceList) {
      const dateStr = o.date || o.delivery?.date || o.pickup?.date || o.created?.date || o.createdAt;
      if (!dateStr) continue;
      const orderDateStr = String(dateStr).replace(' ', 'T').split('T')[0];
      if (!orderDateStr || orderDateStr.length !== 10) continue;
      const orderDate = new Date(orderDateStr + 'T00:00:00.000Z');
      if (isNaN(orderDate.getTime())) continue;
      if (orderDate >= start && orderDate <= end) filtered.push(o);
    }
    const stats = { total: filtered.length, cancelled: 0, completed: 0, active: 0, pending: 0 };
    filtered.forEach((o: any) => {
      const status = (o.status || o._raw?.status || '').toString();
      switch (classifyOrderStatus(status)) {
        case 'cancelled':
          stats.cancelled++;
          break;
        case 'completed':
          stats.completed++;
          break;
        case 'in_progress':
          stats.active++;
          break;
        default:
          stats.active++;
          stats.pending++;
          break;
      }
    });
    return stats;
  }, [zeecoreOrderList, ordersForAlerts, alertsPeriodDateRange, orderStats]);

  // Period-filtered driver stats for Fleet Alerts (violations/compliance in selected time range)
  const periodFilteredDriverStats = useMemo((): { violations: number; driverCount: number; compliancePct: number } | null => {
    const aqr = aquariusData;
    if (!aqr?.driver_details?.length) return null;
    const { start, end } = alertsPeriodDateRange;
    const startDate = new Date(start);
    const endDate = new Date(end);
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);
    let totalViolations = 0;
    let compliantInPeriod = 0;
    for (const driver of aqr.driver_details) {
      const inRange = (d: Date) => d >= startDate && d <= endDate;
      const periodViolations = driver.violations.filter((v) => {
        const violationDate = new Date(v.date);
        return inRange(violationDate);
      });
      totalViolations += periodViolations.length;
      if (periodViolations.length === 0) compliantInPeriod += 1;
    }
    const driverCount = aqr.driver_count ?? aqr.driver_details.length;
    const compliancePct = driverCount > 0 ? Math.round((compliantInPeriod / driverCount) * 100) : 0;
    return { violations: totalViolations, driverCount, compliancePct };
  }, [aquariusData, alertsPeriodDateRange]);

  // Period-filtered fuel stats for Fleet Alerts (litres/cost/transactions in selected time range)
  const periodFilteredFuelStats = useMemo((): { totalLitres: number; totalCost: number; transactionCount: number } | null => {
    const jig = jigsawData;
    if (!jig?.daily_transactions || typeof jig.daily_transactions !== 'object') return null;
    const { start, end } = alertsPeriodDateRange;
    const startDate = new Date(start);
    const endDate = new Date(end);
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);
    let transactionCount = 0;
    let totalLitres = 0;
    let totalCost = 0;
    const fullTxCount = jig.transaction_count || 1;
    const fullLitres = jig.total_fuel_litres || 0;
    const fullCost = jig.total_fuel_cost || 0;
    const spendByDay = jig.daily_spend ?? {};
    const hasDailySpend = Object.keys(spendByDay).length > 0;
    Object.entries(jig.daily_transactions).forEach(([dateStr, count]) => {
      const dateParts = String(dateStr).split('T')[0].split('-');
      if (dateParts.length !== 3) return;
      const transactionDate = new Date(parseInt(dateParts[0], 10), parseInt(dateParts[1], 10) - 1, parseInt(dateParts[2], 10));
      transactionDate.setHours(0, 0, 0, 0);
      if (transactionDate >= startDate && transactionDate <= endDate) {
        transactionCount += count;
        const dayRatio = count / fullTxCount;
        totalLitres += fullLitres * dayRatio;
        if (hasDailySpend) {
          totalCost += Number(spendByDay[dateStr] ?? spendByDay[String(dateStr).split('T')[0]] ?? 0);
        } else {
          totalCost += fullCost * dayRatio;
        }
      }
    });
    return {
      totalLitres: Math.round(totalLitres * 100) / 100,
      totalCost: Math.round(totalCost * 100) / 100,
      transactionCount,
    };
  }, [jigsawData, alertsPeriodDateRange]);

  const applyLiveVehiclesResponse = useCallback((liveData: any): string[] => {
    const allVehicles: any[] = liveData.vehicles ?? [];
    const configuredProviders: string[] = liveData._configuredProviders ?? [];
    setConfiguredTelematicsProviders(configuredProviders);

    const optifleetVehicles = allVehicles.filter((v: any) => !v.source || v.source === 'optifleet');
    const supatrakVehicles = allVehicles.filter((v: any) => v.source === 'supatrak');
    const samsaraVehicles = allVehicles.filter((v: any) => v.source === 'samsara');

    const countOf = (vehicles: any[], status: string) =>
      vehicles.filter((v: any) => v.status === status).length;

    const buildSlice = (vehicles: any[]) => ({
      ...liveData,
      vehicles,
      vehicle_count: vehicles.length,
      position_count: vehicles.length,
      moving_vehicles: countOf(vehicles, 'moving'),
      idle_vehicles: countOf(vehicles, 'idle'),
      prolonged_idle_vehicles: vehicles.filter((v: any) => v.prolonged_idle === true).length,
      idle_with_insufficient_history: vehicles.filter(
        (v: any) => v.status === 'idle' && v.prolonged_idle !== true && v.idle_duration_minutes == null,
      ).length,
      stopped_vehicles: countOf(vehicles, 'stopped'),
      offline_vehicles: countOf(vehicles, 'offline'),
    });

    if (optifleetVehicles.length > 0 || configuredProviders.includes('optifleet')) {
      setOptifleetData(buildSlice(optifleetVehicles));
    } else {
      setOptifleetData(null);
    }

    if (supatrakVehicles.length > 0 || configuredProviders.includes('supatrak')) {
      setSupatrakData(buildSlice(supatrakVehicles));
    } else {
      setSupatrakData(null);
    }

    if (samsaraVehicles.length > 0 || configuredProviders.includes('samsara')) {
      setSamsaraData(buildSlice(samsaraVehicles));
    } else {
      setSamsaraData(null);
    }

    const now = Date.now();
    // The newest position the providers returned (server), not this fetch's time.
    setLiveTelematicsDataCurrentAsOf(typeof liveData?.data_current_as_of === 'string' ? liveData.data_current_as_of : null);
    lastTelematicsPollAtRef.current = now;
    return configuredProviders;
  }, []);

  const fetchLiveTelematics = useCallback(async (options?: { showLoading?: boolean }) => {
    if (telematicsFetchInFlightRef.current) return;
    telematicsFetchInFlightRef.current = true;

    const showLoading = options?.showLoading ?? false;
    if (showLoading) {
      setIsLoading(true);
      setLoadingMessage(telematicsLoadingMsg);
    } else {
      setIsTelematicsRefreshing(true);
    }

    try {
      const liveData = await fetchJson<any>(
        API_ENDPOINTS.fleetIntelligence.vehiclesLive(),
        'Live vehicles',
      );
      applyLiveVehiclesResponse(liveData);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to fetch live telematics';
      if (showLoading) {
        setError(msg);
      } else {
        console.warn('[FleetDataHub] Telematics auto-refresh failed:', msg);
      }
    } finally {
      telematicsFetchInFlightRef.current = false;
      if (showLoading) {
        setIsLoading(false);
        setLoadingMessage(null);
      } else {
        setIsTelematicsRefreshing(false);
      }
    }
  }, [applyLiveVehiclesResponse, telematicsLoadingMsg]);

  // Auto-refresh live GPS every 5 minutes while the Telematics tab is open (pauses when tab hidden).
  useEffect(() => {
    if (useMockData || activeSection !== 'gps') return;

    let cancelled = false;

    const pollIfDue = () => {
      if (cancelled || document.visibilityState === 'hidden') return;
      const elapsed = Date.now() - lastTelematicsPollAtRef.current;
      if (lastTelematicsPollAtRef.current > 0 && elapsed < TELEMATICS_POLL_INTERVAL_MS) {
        return;
      }
      void fetchLiveTelematics();
    };

    // On entering Telematics, refresh only if the last fetch is older than the poll interval.
    pollIfDue();

    const intervalId = window.setInterval(pollIfDue, TELEMATICS_POLL_INTERVAL_MS);

    const onVisibilityChange = () => {
      if (!cancelled && document.visibilityState === 'visible') {
        pollIfDue();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [activeSection, useMockData, fetchLiveTelematics, TELEMATICS_POLL_INTERVAL_MS]);

  // Fetch fleet data from the zeecore truth layer (single source of truth).
  // Default window matches Fleet Alerts period (Today); expand when the user picks 30/180.
  const alertsPeriodToDays = useCallback((period: AlertsPeriod | TimePeriod): number => {
    switch (period) {
      case 'today':
        return 1;
      case 'yesterday':
        return 2;
      case 'last-week':
        return 7;
      case 'month-to-date': {
        const { start, end } = londonMonthToDateRange();
        return Math.max(1, (daysBetweenYmd(start, end) ?? 0) + 1);
      }
      case 'last-month': {
        // Cover previous calendar month from London today (≤ ~62 days).
        const { start } = londonLastMonthRange();
        return Math.max(1, (daysBetweenYmd(start, londonDateString()) ?? 0) + 1);
      }
      case 'last_30_days':
        return 30;
      case 'last_180_days':
        return 180;
      default:
        return 7;
    }
  }, []);

  /**
   * Shape `/fleet-intelligence/orders/list` rows for the Orders section and
   * the period order stats (ordersForAlerts, zeecoreOrderList, qargoData).
   */
  const applyOrdersList = useCallback((raw: any[]) => {
    // Normalize dates. Trino timestamps come back as 'YYYY-MM-DD HH:MM:SS.sss' (space not T).
    // Prefer deliveryDate (DESTINATION stop requested_start) over createdAt for period filtering —
    // users expect "last 7 days" to match orders scheduled for delivery in that window, not orders
    // created in the TMS 7 days ago.
    const mapped = raw.map((o: any) => {
      const ts = o.createdAt || o.date || '';
      const normalized = ts ? String(ts).replace(' ', 'T') : '';
      const dateOnly = normalized ? normalized.split('T')[0] : '';
      const deliveryTs = o.deliveryDate ? String(o.deliveryDate).replace(' ', 'T') : '';
      const deliveryDateOnly = deliveryTs ? deliveryTs.split('T')[0] : '';
      return { ...o, date: deliveryDateOnly || dateOnly, createdAt: normalized, deliveryDate: deliveryDateOnly };
    });
    setOrdersForAlerts(mapped);

    // Map to the Order shape expected by OrdersSection so it can skip the old Qargo endpoint.
    // KPI counts are derived from the same deduped list as the table (below).
    // Palletways: Order ID shown = con_no (externalRef); React key must be entityId (unique).
    // Tracking-note stubs (SCN/ASC/…) often lack customer/delivery/weight — exclude those from
    // the Control Centre list. Deduplicate remaining rows by con_no, keeping the richest record.
    const PALLETWAYS_NOTE_STATUSES = new Set(['SCN', 'ASC', 'GIN', 'GDEP', 'IIN', 'NGT', 'TRACKED']);
    const palletwaysStatusLabel = (status: string): string => {
      const code = (status || '').toUpperCase().trim();
      const labels: Record<string, string> = {
        ENTERED: 'Entered',
        COMPLETED: 'Completed',
        COMPLETE: 'Completed',
        SCN: 'Scanned',
        ASC: 'At Collection Site',
        GIN: 'Goods In',
        GDEP: 'Departed',
        IIN: 'Inbound',
        NGT: 'Night Trunk',
        TRACKED: 'Tracked',
      };
      // Live track statuses (JOB COMPLETE, AT DELIVERY DEPOT, …) pass through unchanged
      return labels[code] || status || '';
    };
    /** True only for delivery-confirmed statuses — not "OUT FOR DELIVERY" / depot / hub. */
    const isPalletwaysCompletedStatus = (status: string): boolean =>
      classifyOrderStatus(status) === 'completed';
    const richness = (o: any): number => {
      let score = 0;
      if (o.deliveryCompany) score += 4;
      if (typeof o.weightKg === 'number' && o.weightKg > 0) score += 4;
      if (o.customerReference) score += 2;
      if (o.originTown || o.originPostcode) score += 2;
      if (o.destinationTown || o.destinationPostcode) score += 2;
      // Prefer delivery-confirmed status over Entered when merging rows
      if (isPalletwaysCompletedStatus(String(o.status || ''))) score += 50;
      else if (String(o.status || '').toLowerCase() === 'entered') score += 3;
      // Live track statuses outrank Entered but not completed
      else if (String(o.status || '').trim()) score += 20;
      if (o.createdAt) score += 1;
      return score;
    };

    const formatPlace = (town?: string | null, postcode?: string | null): string =>
      [town, postcode].map((s) => String(s || '').trim()).filter(Boolean).join(', ');

    const listRows = mapped
      .filter((o: any) => {
        if (o.source !== 'palletways') return true;
        const code = String(o.status || '').toUpperCase();
        const isNoteStub = PALLETWAYS_NOTE_STATUSES.has(code)
          && !o.deliveryCompany
          && !(typeof o.weightKg === 'number' && o.weightKg > 0);
        return !isNoteStub;
      })
      .map((o: any) => {
        const isPalletways = o.source === 'palletways';
        const isSameDay = o.source === 'same_day' || o.source === 'declared';
        const originLabel = formatPlace(o.originTown, o.originPostcode);
        const destinationLabel = formatPlace(o.destinationTown, o.destinationPostcode);
        const sameDayStatusLabel = (status: string): string => {
          const bucket = classifyOrderStatus(status);
          if (bucket === 'completed') return 'Completed';
          if (bucket === 'cancelled') return 'Cancelled';
          // Tracker has no delivery status until GPS confirms collection + delivery
          return 'Declared';
        };
        return {
          // Unique key for React reconciliation (must NOT be shared con_no)
          id: o.entityId || `${o.source || 'order'}:${o.externalRef || Math.random()}`,
          // Display Order ID (con_no for Palletways; tracker row / chain id for Same Day)
          name: o.externalRef || o.entityId || '',
          status: isPalletways
            ? palletwaysStatusLabel(o.status || '')
            : isSameDay
              ? sameDayStatusLabel(o.status || '')
              : (o.status || ''),
          date: o.date,
          customerRef: o.customerReference || '',
          pickup: (originLabel || o.originTown)
            ? {
                name: o.originTown || undefined,
                address: originLabel || undefined,
                postcode: o.originPostcode || undefined,
              }
            : undefined,
          delivery: (destinationLabel || o.destinationTown || o.deliveryCompany)
            ? {
                // Prefer place (town) for display; keep company as fallback name for Qargo-style rows
                name: o.destinationTown || o.deliveryCompany || undefined,
                address: destinationLabel || o.deliveryCompany || undefined,
                postcode: o.destinationPostcode || undefined,
              }
            : undefined,
          weightKg: typeof o.weightKg === 'number' ? o.weightKg : undefined,
          lifts: typeof o.lifts === 'number' ? o.lifts : undefined,
          type: o.type || undefined,
          created: { date: o.date },
          source: o.source === 'declared' ? 'same_day' : (o.source || 'unknown'),
          trackingId: isPalletways ? String(o.trackingId || '') : undefined,
          podReference: o.podReference || undefined,
          cancelledAt: o.cancelledAt || undefined,
          cancellationReason: o.cancellationReason || undefined,
          lastArrivedAt: o.lastArrivedAt || undefined,
          lastCustodyTransferAt: o.lastCustodyTransferAt || undefined,
          custodyOperatorEntityId: o.custodyOperatorEntityId || undefined,
          _raw: o,
        };
      });

    // One row per Palletways con_no (keep richest); other sources keep all unique ids
    const deduped: typeof listRows = [];
    const seenPalletwaysRef = new Map<string, number>();
    for (const row of listRows) {
      if (row.source !== 'palletways') {
        deduped.push(row);
        continue;
      }
      const ref = String(row.name || '').trim();
      if (!ref) {
        deduped.push(row);
        continue;
      }
      const existingIdx = seenPalletwaysRef.get(ref);
      if (existingIdx === undefined) {
        seenPalletwaysRef.set(ref, deduped.length);
        deduped.push(row);
      } else {
        const existing = deduped[existingIdx];
        // Keep richest consignment fields; prefer delivery-confirmed status over Entered,
        // but preserve the actual live label (e.g. JOB COMPLETE) — do not coerce to "Completed".
        const rowCompleted = isPalletwaysCompletedStatus(row.status);
        const existingCompleted = isPalletwaysCompletedStatus(existing.status);
        const richer = richness(row._raw) >= richness(existing._raw) ? row : existing;
        const thinner = richer === row ? existing : row;
        const coalesce = (...vals: unknown[]) => {
          for (const v of vals) {
            if (v == null) continue;
            if (typeof v === 'string' && v.trim() === '') continue;
            return v;
          }
          return undefined;
        };
        const mergedStatus = rowCompleted && !existingCompleted
          ? row.status
          : existingCompleted && !rowCompleted
            ? existing.status
            : richer.status;
        const mergedRawStatus = rowCompleted && !existingCompleted
          ? row._raw?.status
          : existingCompleted && !rowCompleted
            ? existing._raw?.status
            : (richer._raw?.status ?? thinner._raw?.status);
        deduped[existingIdx] = {
          ...thinner,
          ...richer,
          status: mergedStatus,
          customerRef: coalesce(richer.customerRef, thinner.customerRef) as string || '',
          type: coalesce(richer.type, thinner.type) as string || '',
          weightKg: (coalesce(richer.weightKg, thinner.weightKg) as number | undefined),
          pickup: richer.pickup?.name || thinner.pickup?.name
            ? {
                name: (coalesce(richer.pickup?.name, thinner.pickup?.name) as string) || '',
                address: coalesce(richer.pickup?.address, thinner.pickup?.address) as string | undefined,
                postcode: coalesce(richer.pickup?.postcode, thinner.pickup?.postcode),
              }
            : richer.pickup || thinner.pickup,
          delivery: richer.delivery?.name || thinner.delivery?.name
            ? {
                name: (coalesce(richer.delivery?.name, thinner.delivery?.name) as string) || '',
                address: coalesce(richer.delivery?.address, thinner.delivery?.address) as string | undefined,
                postcode: coalesce(richer.delivery?.postcode, thinner.delivery?.postcode),
              }
            : richer.delivery || thinner.delivery,
          _raw: {
            ...(thinner._raw || {}),
            ...(richer._raw || {}),
            status: mergedRawStatus,
            customerReference: coalesce(richer._raw?.customerReference, thinner._raw?.customerReference),
            type: coalesce(richer._raw?.type, thinner._raw?.type),
            weightKg: coalesce(richer._raw?.weightKg, thinner._raw?.weightKg),
            originTown: coalesce(richer._raw?.originTown, thinner._raw?.originTown),
            originPostcode: coalesce(richer._raw?.originPostcode, thinner._raw?.originPostcode),
            destinationTown: coalesce(richer._raw?.destinationTown, thinner._raw?.destinationTown),
            destinationPostcode: coalesce(richer._raw?.destinationPostcode, thinner._raw?.destinationPostcode),
          },
        };
      }
    }

    setZeecoreOrderList(deduped);

    // Refresh KPI counts from the same deduped list the table uses
    const tableByDate: Record<string, number> = {};
    deduped.forEach((o: any) => {
      if (o.date && o.date.length === 10) {
        tableByDate[o.date] = (tableByDate[o.date] || 0) + 1;
      }
    });
    let tableCompleted = 0;
    let tableCancelled = 0;
    let tableInProgress = 0;
    let tablePending = 0;
    deduped.forEach((o: any) => {
      switch (classifyOrderStatus(o.status || o._raw?.status)) {
        case 'completed':
          tableCompleted += 1;
          break;
        case 'cancelled':
          tableCancelled += 1;
          break;
        case 'in_progress':
          tableInProgress += 1;
          break;
        default:
          tablePending += 1;
      }
    });
    setQargoData({
      order_count: deduped.length,
      company_count: 0,
      resource_count: 0,
      completed_orders: tableCompleted,
      in_progress_orders: tableInProgress,
      pending_orders: tablePending,
      cancelled_orders: tableCancelled,
      delivery_performance: deduped.length > 0 ? Math.round((tableCompleted / deduped.length) * 100) : 0,
      orders_by_date: tableByDate,
    });
  }, []);

  const fetchFromZeecore = useCallback(async (
    windowDays?: number,
    options?: { quiet?: boolean },
  ) => {
    const quiet = Boolean(options?.quiet);
    if (!quiet) {
      setIsLoading(true);
      setError(null);
    }
    try {
      const now = new Date();
      const to = now.toISOString();
      const days = Math.max(1, windowDays ?? alertsPeriodToDays(alertsPeriod));
      const from = new Date(now.getTime() - days * 24 * 60 * 60 * 1000).toISOString();
      const timeRange = { from, to };
      // Drivers are not in this rolling window: they load for the alerts
      // period from the stored Driver tab view (hub drivers effect below).
      setHubDriversReloadKey((k) => k + 1);
      // The orders list is the slowest read here (several rounds of live
      // queries). Where nothing on screen needs it yet it is skipped and
      // loaded on demand (ordersListWanted below).
      const includeOrdersList = ordersListWantedRef.current;
      const readStartedAt = Date.now();
      if (includeOrdersList) ordersListRequestedAtRef.current = readStartedAt;
      const [summaryRes, fuelRes, vehiclesRes, ordersRes] = await Promise.allSettled([
        fetchJson<any>(API_ENDPOINTS.fleetIntelligence.summary(timeRange), 'Fleet summary'),
        fetchJson<any>(API_ENDPOINTS.fleetIntelligence.fuelSummary(timeRange), 'Fuel summary'),
        fetchJson<any>(API_ENDPOINTS.fleetIntelligence.vehiclesLive(), 'Live vehicles'),
        includeOrdersList
          ? fetchJson<any>(API_ENDPOINTS.fleetIntelligence.orders.list(timeRange), 'Orders list')
          : Promise.resolve(null),
      ]);
      loadedHubWindowDaysRef.current = Math.max(loadedHubWindowDaysRef.current, days);
      const results = includeOrdersList
        ? [summaryRes, fuelRes, vehiclesRes, ordersRes]
        : [summaryRes, fuelRes, vehiclesRes];
      const failures = results.filter((result): result is PromiseRejectedResult => result.status === 'rejected');
      if (failures.length === results.length) {
        throw failures[0].reason;
      }

      // Summary → AnalyticsOverview + orderStats
      if (summaryRes.status === 'fulfilled' && summaryRes.value) {
        const s = summaryRes.value;
        const vTotal: number = s.vehicles?.total ?? 0;
        const vActive: number = s.vehicles?.recentlyActive ?? 0;
        const overview: AnalyticsOverview = {
          timestamp: new Date().toISOString(),
          overall: {
            total_vehicles: vTotal,
            total_drivers: s.drivers?.total ?? 0,
            total_orders: s.orders?.total ?? 0,
            total_fuel_transactions: s.fuel?.transactionCount ?? 0,
            fleet_health_score: vTotal > 0 ? Math.round((vActive / vTotal) * 100) : 0,
            compliance_score: s.drivers?.compliancePct ?? 0,
          },
          apis: {},
          alerts: [],
        };
        setAnalyticsOverview(overview);
        setOrderStats({
          total: s.orders?.total ?? 0,
          cancelled: s.orders?.cancelled ?? 0,
          completed: s.orders?.completed ?? 0,
          // "Active" = anything not yet completed or cancelled. inProgress alone
          // is 0 for sources (e.g. Palletways) whose raw status vocabulary never
          // contains PROGRESS/TRANSIT/ACTIVE — pending (entered/declared, not yet
          // delivered) must count too. Matches periodFilteredOrderStats' and
          // OrdersSection's activeOrders = inProgressOrders + pendingOrders.
          active: (s.orders?.inProgress ?? 0) + (s.orders?.pending ?? 0),
          pending: s.orders?.pending ?? 0,
        });
      }

      // Fuel → jigsawData (response already maps to JigsawData shape)
      if (fuelRes.status === 'fulfilled' && fuelRes.value) {
        setJigsawData(fuelRes.value);
      }

      // Vehicles → split by source provider so the source filter in GPSTracking works.
      let liveConfiguredProviders: string[] = [];
      if (vehiclesRes.status === 'fulfilled' && vehiclesRes.value) {
        liveConfiguredProviders = applyLiveVehiclesResponse(vehiclesRes.value);
      }

      // Legacy Supatrak fallback (:4008) only for orgs that use Supatrak — skip Samsara-only pilots.
      const needsSupatrakFallback =
        liveConfiguredProviders.length === 0
        || liveConfiguredProviders.includes('supatrak');
      if (needsSupatrakFallback) {
        await fetchSupatrakDirect();
      }

      // Orders list → ordersForAlerts + qargoData + zeecoreOrderList (for OrdersSection)
      if (includeOrdersList) {
        if (ordersRes.status === 'fulfilled' && ordersRes.value?.orders) {
          applyOrdersList(ordersRes.value.orders);
          ordersListNeedsLoadRef.current = false;
        }
      } else if (ordersListRequestedAtRef.current < readStartedAt) {
        // Skipped (loaded on demand) and no list request has started since:
        // the list on screen is older than this read, so fetch it again the
        // next time it is wanted.
        ordersListNeedsLoadRef.current = true;
        setOrdersListSkipKey((k) => k + 1);
      }

      if (failures.length > 0) {
        const messages = failures.map((failure) =>
          failure.reason instanceof Error ? failure.reason.message : String(failure.reason)
        );
        if (quiet) {
          console.warn('[FleetDataHub] Quiet hub widen partial failure:', messages.join('; '));
        } else {
          setError(`Some fleet data could not be loaded: ${messages.join('; ')}`);
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to fetch fleet data';
      const isNetwork = /fetch|network|failed|ECONNREFUSED|ETIMEDOUT/i.test(msg) || (err instanceof TypeError && err.message.includes('fetch'));
      if (quiet) {
        console.warn('[FleetDataHub] Quiet hub window widen failed:', msg);
      } else {
        setError(isNetwork
          ? 'Cannot reach the fleet data service. Please try again shortly, or contact your ZeeFleet administrator if this continues.'
          : msg
        );
      }
    } finally {
      if (!quiet) setIsLoading(false);
    }
  }, [applyLiveVehiclesResponse, applyOrdersList, fetchSupatrakDirect, alertsPeriod, alertsPeriodToDays]);

  /**
   * Tracking-note → Completed writes finish in raw-stored after the import job
   * reports complete. One immediate fetch often still shows Completed = 0.
   * First fetch updates the UI; follow-up fetches pick up Completed as it lands.
   */
  const settleFetchFromZeecore = useCallback(async () => {
    await fetchFromZeecore();
    void (async () => {
      for (const delay of [20_000, 40_000, 60_000]) {
        await wait(delay);
        try {
          await fetchFromZeecore(undefined, { quiet: true });
        } catch (err) {
          console.warn('[Refresh] Settle re-fetch failed:', err);
        }
      }
    })();
  }, [fetchFromZeecore]);

  // After Data Import backfill/incremental succeeds, re-read truth layer (no re-ingest).
  useEffect(() => {
    if (useMockData) return;

    const onIngestComplete = () => {
      consumeControlCentreIngestRefresh();
      void settleFetchFromZeecore();
    };

    window.addEventListener(CONTROL_CENTRE_INGEST_REFRESH_EVENT, onIngestComplete);

    // Control Centre may not have been mounted when the job finished
    if (consumeControlCentreIngestRefresh()) {
      void settleFetchFromZeecore();
    }

    return () => {
      window.removeEventListener(CONTROL_CENTRE_INGEST_REFRESH_EVENT, onIngestComplete);
    };
  }, [useMockData, settleFetchFromZeecore]);

  // Widen hub history when Fleet Alerts / Orders period needs more days than already loaded.
  // Pass quiet: true for period chips so the Orders tab does not hard-refresh (no isLoading).
  const ensureHubWindowDays = useCallback((days: number, options?: { quiet?: boolean }) => {
    const needed = Math.max(1, Math.min(Math.ceil(days), 180));
    if (needed <= loadedHubWindowDaysRef.current) return;
    hubWidenPendingDaysRef.current = Math.max(hubWidenPendingDaysRef.current, needed);
    if (hubWidenInFlightRef.current) return;

    const quiet = Boolean(options?.quiet);
    hubWidenInFlightRef.current = true;
    void (async () => {
      try {
        while (hubWidenPendingDaysRef.current > loadedHubWindowDaysRef.current) {
          const daysToFetch = hubWidenPendingDaysRef.current;
          hubWidenPendingDaysRef.current = 0;
          await fetchFromZeecore(daysToFetch, { quiet });
        }
      } finally {
        hubWidenInFlightRef.current = false;
        if (hubWidenPendingDaysRef.current > loadedHubWindowDaysRef.current) {
          ensureHubWindowDays(hubWidenPendingDaysRef.current, { quiet });
        }
      }
    })();
  }, [fetchFromZeecore]);

  // Orders list on demand: load it when it becomes wanted (Orders section
  // opened, or sources say the Overview needs it) and the hub read skipped it.
  const ordersListLoadSeq = useRef(0);
  useEffect(() => {
    if (!ordersListWanted || !ordersListNeedsLoadRef.current) return;
    const days = Math.max(1, loadedHubWindowDaysRef.current || alertsPeriodToDays(alertsPeriod));
    const seq = ++ordersListLoadSeq.current;
    const now = new Date();
    ordersListRequestedAtRef.current = now.getTime();
    const timeRange = {
      from: new Date(now.getTime() - days * 24 * 60 * 60 * 1000).toISOString(),
      to: now.toISOString(),
    };
    setIsLoading(true);
    void (async () => {
      try {
        const res = await fetchJson<{ orders?: unknown[] }>(API_ENDPOINTS.fleetIntelligence.orders.list(timeRange), 'Orders list');
        if (seq !== ordersListLoadSeq.current) return;
        if (res?.orders) {
          applyOrdersList(res.orders);
          ordersListNeedsLoadRef.current = false;
        }
      } catch (err) {
        if (seq !== ordersListLoadSeq.current) return;
        setError(`Some fleet data could not be loaded: ${err instanceof Error ? err.message : String(err)}`);
      } finally {
        if (seq === ordersListLoadSeq.current) setIsLoading(false);
      }
    })();
    // alertsPeriod is read for the first window only; widening goes through fetchFromZeecore.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordersListWanted, ordersListSkipKey, applyOrdersList]);

  useEffect(() => {
    const needed = alertsPeriodToDays(alertsPeriod);
    if (loadedHubWindowDaysRef.current > 0 && needed > loadedHubWindowDaysRef.current) {
      ensureHubWindowDays(needed, { quiet: true });
    }
  }, [alertsPeriod, alertsPeriodToDays, ensureHubWindowDays]);

  /** Orders tab period change → sync alerts period; widen hub quietly if needed. */
  const handleOrdersHistoryWindowChange = useCallback((
    period: TimePeriod,
    range?: { start?: string; end?: string },
  ) => {
    if (
      period === 'today'
      || period === 'yesterday'
      || period === 'last-week'
      || period === 'month-to-date'
      || period === 'last-month'
      || period === 'last_30_days'
      || period === 'last_180_days'
    ) {
      setAlertsPeriod(period);
      // Today/Yesterday volume chart uses a 7-day context — prefetch quietly so
      // switching chips only reloads orders_daily gauges, not the whole hub UI.
      const days = alertsPeriodToDays(period);
      const needed = (period === 'today' || period === 'yesterday' || period === 'last-week')
        ? Math.max(days, ORDERS_VOLUME_CHART_DAYS)
        : days;
      ensureHubWindowDays(needed, { quiet: true });
      return;
    }

    if (period === 'all-time') {
      ensureHubWindowDays(180, { quiet: true });
      return;
    }

    if (period === 'custom' && range?.start) {
      const startMs = Date.parse(`${range.start}T00:00:00.000Z`);
      if (Number.isFinite(startMs)) {
        const daysBack = Math.ceil((Date.now() - startMs) / (24 * 60 * 60 * 1000)) + 1;
        ensureHubWindowDays(daysBack, { quiet: true });
      } else {
        ensureHubWindowDays(180, { quiet: true });
      }
    }
  }, [alertsPeriodToDays, ensureHubWindowDays]);

  // Refresh a single API
  const refreshSingleApi = useCallback(async (
    apiId: string,
    options?: { deepRefresh?: boolean },
  ) => {
    // Prevent multiple simultaneous refresh operations
    if (isRefreshing) {
      console.log('[Refresh] Already refreshing, skipping...');
      return;
    }
    
    setIsRefreshing(true);
    setError(null);
    setShowRefreshDropdown(false);

    // Initialize this API as pending
    setRefreshStatus({ [apiId]: 'pending' });
    
    // Track if component is still mounted
    let isMounted = true;
    
    try {
      const api = apiInfo.find(a => a.id === apiId);
      if (!api) {
        throw new Error(`Unknown API: ${apiId}`);
      }
      
      // Update current API to loading
      setRefreshStatus({ [apiId]: 'loading' });

      // Truth-layer sources (Driver UI reads ZeeCore driversSummary, not the Python aquarius export)
      if (apiId === 'palletways' || apiId === 'samsara' || apiId === 'aquarius') {
        try {
          if (apiId === 'aquarius') {
            // Driver refresh: Aquarius roster/T&A/compliance + Samsara EU tachograph
            // hours when licensed (Darcica). Scope keeps GPS/faults off this path —
            // those stay on Telematics / Samsara / Maintenance refresh.
            const hasSamsara = enabledIngestSources.includes('samsara');
            const driverSources = hasSamsara ? ['aquarius', 'samsara'] : ['aquarius'];
            await runIncrementalImport(driverSources, {
              ...options,
              ...(hasSamsara ? { samsaraScope: 'tachograph' as const } : {}),
            });
          } else {
            await runIncrementalImport([apiId], options);
          }
        } catch (incrErr) {
          if (apiId === 'palletways') {
            console.warn('[Refresh] Palletways incremental failed, trying direct ingest:', incrErr);
            await runPalletwaysDirectIngest(options);
            // Allow queue workers a short window to process
            await wait(5000);
          } else {
            throw incrErr;
          }
        }
        if (!isMounted) return;
        await settleFetchFromZeecore();
        if (isMounted) {
          setRefreshStatus({ [apiId]: 'complete' });
          setError(null);
        }
        return;
      }
      
      try {
        // Call individual API refresh endpoint with authentication
        let response: Response | null = null;
        let usedFallback = false;
        
        try {
          response = await authenticatedFetch(`${API_BASE_URL}/api/analytics/refresh/${apiId}`, {
            method: 'POST'
          });
        } catch (authError) {
          console.warn(`[Refresh] Auth endpoint failed for ${api.name}, trying fallback:`, authError);
          // For Orders, use run-export endpoint which doesn't require auth
          if (apiId === 'qargo') {
            try {
              response = await authenticatedFetch(API_ENDPOINTS.qargoRunExport(), { method: 'POST' });
              usedFallback = true;
            } catch (fallbackError) {
              throw new Error(`Both authenticated and fallback endpoints failed: ${authError instanceof Error ? authError.message : String(authError)}`);
            }
          } else {
            throw authError;
          }
        }
        
        if (!isMounted) return;
        
        if (!response) {
          throw new Error('No response received from server');
        }
        
        // Read response body first (even for errors) to get detailed error message
        let result: any;
        try {
          const responseText = await response.text();
          if (!responseText) {
            throw new Error('Empty response from server');
          }
          result = JSON.parse(responseText);
        } catch (parseError) {
          console.error(`[Refresh] Failed to parse response for ${api.name}:`, parseError);
          throw new Error(`Invalid response from server: ${response.status} ${response.statusText}`);
        }
        
        if (!isMounted) return;
        
        if (!response.ok) {
          const errorMsg = result.error || result.message || result.result?.error || `HTTP ${response.status}: ${response.statusText}`;
          throw new Error(errorMsg);
        }
        
        // Check if the refresh was successful. `result` may come from either
        // POST /api/analytics/refresh/:api or the qargo run-export fallback —
        // both use envelope key `ok`. `result.result?.success` is the nested
        // per-script business result (runPythonScript's own field, distinct
        // from the HTTP envelope).
        const isSuccess = result.ok === true || result.result?.success === true;
        
        // Update status based on result
        if (isMounted) {
          setRefreshStatus({ [apiId]: isSuccess ? 'complete' : 'error' });
        }
        
        if (!isSuccess) {
          const errorMsg = result.result?.error || result.error || result.message || 'Unknown error';
          console.error(`[Refresh] ${api.name} failed:`, errorMsg);
          console.error(`[Refresh] ${api.name} full result:`, result);
        } else {
          if (isMounted) {
            setError(null);
          }
          console.log(`[Refresh] ${api.name} completed successfully${usedFallback ? ' (via fallback)' : ''}`);
        }
        
      } catch (err) {
        if (!isMounted) return;
        
        let errorMessage = 'Load failed';
        if (err instanceof Error) {
          errorMessage = err.message;
        } else if (typeof err === 'string') {
          errorMessage = err;
        } else if (err && typeof err === 'object') {
          errorMessage = (err as any).error || (err as any).message || JSON.stringify(err);
        }
        
        setRefreshStatus({ [apiId]: 'error' });
        
        if (errorMessage.includes('401') || errorMessage.includes('Authentication')) {
          console.error(`[Refresh] ${api.name} auth error - using Mock data or local exports instead:`, errorMessage);
        } else if (errorMessage.includes('ECONNREFUSED')) {
          console.error(`[Refresh] ${api.name} - backend not running. Start server or use Mock data:`, errorMessage);
        } else {
          console.error(`[Refresh] ${api.name} error:`, errorMessage);
          console.error(`[Refresh] ${api.name} full error object:`, err);
        }
      }
      
      if (!isMounted) return;
      
      // Fetch the updated data for this specific API
      if (apiId !== 'palletways' && apiId !== 'samsara') {
        await fetchApiData(apiId as 'jigsaw' | 'optifleet' | 'supatrak' | 'qargo' | 'aquarius');
      }
      
      if (!isMounted) return;
      
      // If it's qargo, also refresh order stats
      if (apiId === 'qargo') {
        await fetchOrderStats();
      }
      
      // Re-fetch from zeecore truth layer to pick up newly ingested data
      await fetchFromZeecore();

      if (!isMounted) return;
      
      setError(null);
      
    } catch (err) {
      if (!isMounted) return;
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      console.error('[Refresh] Single API refresh error:', err);
    } finally {
      if (isMounted) {
        setIsRefreshing(false);
      }
    }
  }, [apiInfo, enabledIngestSources, fetchApiData, fetchFromZeecore, settleFetchFromZeecore, fetchOrderStats, isRefreshing, runIncrementalImport, runPalletwaysDirectIngest]);

  // Refresh LIVE data via ZeeCore incremental ingest for all enabled org sources.
  // Routine (default): short lookbacks (Aquarius activity/maint/violations 2d,
  // Palletways 3d), no depot sync, no Palletways enrichment.
  // Deep: 7-day catch-up with enrichment / depots.
  const refreshLiveData = useCallback(async (
    selectedApiId?: string,
    options?: { deepRefresh?: boolean },
  ) => {
    // If a specific API is selected, use single API refresh
    if (selectedApiId) {
      return refreshSingleApi(selectedApiId, options);
    }
    
    // Prevent multiple simultaneous refresh operations
    if (isRefreshing) {
      console.log('[Refresh] Already refreshing, skipping...');
      return;
    }
    
    setIsRefreshing(true);
    setError(null);
    setShowRefreshDropdown(false);

    // Show all sources as loading while incremental import runs (incl. Aquarius maint)
    const loadingStatus: RefreshStatus = {};
    apiInfo.forEach(api => { loadingStatus[api.id] = 'loading'; });
    loadingStatus[MAINTENANCE_REFRESH_SOURCE.id] = 'loading';
    setRefreshStatus(loadingStatus);

    let isMounted = true;

    try {
      await runIncrementalImport(undefined, options);

      // Reload after import; keep settling while notes → Completed land in Iceberg.
      await settleFetchFromZeecore();

      if (!isMounted) return;

      const doneStatus: RefreshStatus = {};
      apiInfo.forEach((api) => { doneStatus[api.id] = 'complete'; });
      doneStatus[MAINTENANCE_REFRESH_SOURCE.id] = 'complete';
      setRefreshStatus(doneStatus);
      setError(null);

    } catch (err) {
      if (!isMounted) return;

      const errorMsg = err instanceof Error ? err.message : 'Failed to start incremental import';
      console.error('[Refresh] Incremental import error:', errorMsg);
      setError(errorMsg);

      const errStatus: RefreshStatus = {};
      apiInfo.forEach((api) => { errStatus[api.id] = 'error'; });
      errStatus[MAINTENANCE_REFRESH_SOURCE.id] = 'error';
      setRefreshStatus(errStatus);
    } finally {
      if (isMounted) {
        setIsRefreshing(false);
      }
    }
  }, [apiInfo, fetchFromZeecore, settleFetchFromZeecore, isRefreshing, refreshSingleApi, runIncrementalImport]);

  /**
   * Refresh only Maintenance tab sources:
   * - Aquarius maintenance — 90-day windowed catch-up (completed / defect checks)
   *   in chunks, then one snapshot (upcoming / overdue / VOR). Not full Aquarius
   *   (no ta-shift / activity).
   * - Samsara fault-codes history (when enabled) — short 2-day window for Health.
   * Skips driver activity, orders, fuel, full telematics, etc.
   */
  const refreshMaintenanceData = useCallback(async () => {
    if (isRefreshing) {
      console.log('[Refresh] Already refreshing, skipping maintenance-only…');
      return;
    }

    const runId = ++maintenanceRefreshGenRef.current;
    const stillActive = () => runId === maintenanceRefreshGenRef.current;

    setIsRefreshing(true);
    setError(null);
    setRefreshDetailMessage(null);
    setShowRefreshDropdown(false);
    setRefreshStatus({ maintenance: 'loading' });

    const base = API_ENDPOINTS.base || '';
    const INGEST_TIMEOUT_MS = 120_000;
    const hasAquarius = enabledIngestSources.length === 0 || enabledIngestSources.includes('aquarius');
    const hasSamsara = enabledIngestSources.includes('samsara');
    const ymdUtc = (d: Date) => d.toISOString().split('T')[0];

    try {
      const now = new Date();
      const endStr = ymdUtc(now);
      const historyStart = new Date(now);
      historyStart.setUTCDate(historyStart.getUTCDate() - (MAINTENANCE_SCHEDULE_HISTORY_DAYS - 1));
      const faultStart = new Date(now);
      faultStart.setUTCDate(faultStart.getUTCDate() - 2);
      const faultStartTime = `${ymdUtc(faultStart)}T00:00:00.000Z`;
      const faultEndTime = now.toISOString();

      const aquariusWarnings: string[] = [];

      const tasks: Array<Promise<void>> = [];

      if (hasAquarius) {
        tasks.push((async () => {
          const postMaintenance = async (
            startDate: string,
            endDate: string,
            feeds: 'windowed' | 'snapshot',
          ) => {
            const res = await authenticatedFetch(`${base}/ingest/aquarius/maintenance`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ startDate, endDate, feeds }),
            }, INGEST_TIMEOUT_MS);
            if (!res.ok) {
              const data = await res.json().catch(() => ({})) as { message?: string; error?: string };
              throw new Error(
                data.message ?? data.error ?? `Aquarius maintenance (${feeds}) failed (${res.status})`,
              );
            }
          };

          // Build windowed chunks first so progress denominators are stable.
          const chunks: Array<{ start: string; end: string }> = [];
          let chunkStart = new Date(historyStart);
          while (chunkStart <= now) {
            const chunkEnd = new Date(chunkStart);
            chunkEnd.setUTCDate(
              chunkEnd.getUTCDate() + (MAINTENANCE_REFRESH_WINDOWED_CHUNK_DAYS - 1),
            );
            if (chunkEnd > now) chunkEnd.setTime(now.getTime());
            chunks.push({ start: ymdUtc(chunkStart), end: ymdUtc(chunkEnd) });
            chunkStart = new Date(chunkEnd);
            chunkStart.setUTCDate(chunkStart.getUTCDate() + 1);
          }

          for (let i = 0; i < chunks.length; i++) {
            if (!stillActive()) return;
            const { start, end } = chunks[i];
            setRefreshDetailMessage(
              `Aquarius history ${i + 1}/${chunks.length} (${start} → ${end})…`,
            );
            try {
              await postMaintenance(start, end, 'windowed');
            } catch (err) {
              const msg = err instanceof Error ? err.message : String(err);
              aquariusWarnings.push(`history chunk ${i + 1}/${chunks.length}: ${msg}`);
              console.warn('[Refresh] Aquarius windowed chunk failed:', msg);
            }
          }

          // Always attempt snapshot so upcoming/overdue/VOR update even if a history chunk failed.
          if (!stillActive()) return;
          setRefreshDetailMessage('Aquarius snapshot (upcoming / overdue / VOR)…');
          try {
            await postMaintenance(endStr, endStr, 'snapshot');
          } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            aquariusWarnings.push(`snapshot: ${msg}`);
            console.warn('[Refresh] Aquarius snapshot failed:', msg);
          }

          if (aquariusWarnings.length === chunks.length + 1) {
            throw new Error(`Aquarius maintenance failed: ${aquariusWarnings.join('; ')}`);
          }

          console.log(
            `[Refresh] Aquarius maintenance catch-up complete (${MAINTENANCE_SCHEDULE_HISTORY_DAYS}d windowed + snapshot)`,
          );
        })());
      }

      if (hasSamsara) {
        tasks.push((async () => {
          if (!stillActive()) return;
          setRefreshDetailMessage((prev) => prev ?? 'Samsara fault codes (2 days)…');
          const res = await authenticatedFetch(`${base}/ingest/samsara/fault-codes`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              mode: 'history',
              startTime: faultStartTime,
              endTime: faultEndTime,
            }),
          }, INGEST_TIMEOUT_MS);
          if (!res.ok) {
            const data = await res.json().catch(() => ({})) as { message?: string; error?: string };
            throw new Error(data.message ?? data.error ?? `Samsara fault-codes failed (${res.status})`);
          }
          console.log('[Refresh] Samsara fault-codes history queued (2-day window)');
          await wait(5000);
        })());
      }

      if (tasks.length === 0) {
        throw new Error('No Aquarius or Samsara sources enabled for maintenance refresh');
      }

      const results = await Promise.allSettled(tasks);
      if (!stillActive()) return;

      const failures = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
      if (failures.length === results.length) {
        throw failures[0].reason instanceof Error
          ? failures[0].reason
          : new Error(String(failures[0].reason));
      }

      const partialNotes: string[] = [...aquariusWarnings];
      for (const f of failures) {
        const msg = f.reason instanceof Error ? f.reason.message : String(f.reason);
        partialNotes.push(msg);
        console.warn('[Refresh] Partial maintenance refresh failure:', f.reason);
      }

      await settleFetchFromZeecore();
      if (!stillActive()) return;

      setMaintenanceIngestRefreshKey((k) => k + 1);
      setActiveSection('maintenance');
      setRefreshStatus({ maintenance: 'complete' });
      setRefreshDetailMessage(null);
      if (partialNotes.length > 0) {
        setError(`Maintenance refresh finished with warnings: ${partialNotes.join('; ')}`);
      } else {
        setError(null);
      }
    } catch (err) {
      if (!stillActive()) return;
      const errorMsg = err instanceof Error ? err.message : 'Maintenance refresh failed';
      console.error('[Refresh] Maintenance refresh error:', errorMsg);
      setError(errorMsg);
      setRefreshStatus({ maintenance: 'error' });
      setRefreshDetailMessage(null);
    } finally {
      if (stillActive()) setIsRefreshing(false);
    }
  }, [isRefreshing, enabledIngestSources, settleFetchFromZeecore]);

  useEffect(() => {
    return () => {
      maintenanceRefreshGenRef.current += 1;
    };
  }, []);

  // Fetch fleet data from the zeecore truth layer (single source of truth).
  // Window matches Fleet Alerts period; expands when the user picks a longer range.
  useEffect(() => {
    fetchFromZeecore();
    // Drive Refresh All / Orders menu from org-enabled ingest sources (includes palletways)
    fetchEnabledIngestSources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount


  const renderSection = () => {
    switch (activeSection) {
      case 'overview':
        const overviewProps: FleetHubOverviewProps = {
          analyticsOverview: useMockData ? MOCK_ANALYTICS_OVERVIEW : analyticsOverview,
          dashboardOverview: useMockData ? MOCK_DASHBOARD_OVERVIEW : dashboardOverview,
          onFetchData: fetchFromZeecore,
          isLoading,
          onNavigate: (section, kpi) => {
            setHighlightKPI(kpi || null);
            setActiveSection(section);
          },
          useMockData,
          telematicsTotals: useMockData ? undefined : telematicsTotals,
          active: activeTab === DEFAULT_HUB_TAB_ID,
          ordersDailyEnabled,
          ordersUnavailableMessage,
        };
        // Telematics totals come from computedTelematicsTotals (same merge logic as GPSTracking).
        return <FleetHubOverview key={useMockData ? 'demo' : 'live'} {...overviewProps} />;
      case 'fuel':
        return (
          <FuelAnalytics
            data={useMockData ? MOCK_JIGSAW_DATA : jigsawData}
            apiStatus={useMockData ? MOCK_DASHBOARD_OVERVIEW.apis.jigsaw : dashboardOverview?.apis?.jigsaw}
            onFetchData={() => fetchApiData('jigsaw')}
            isLoading={isLoading}
            highlightKPI={highlightKPI}
            initialPeriod={alertsPeriod}
            useMockData={useMockData}
            fuelSourceLabel={fuelSourceLabel}
            fuelSourceIds={fuelIngestSources}
            onHighlightConsumed={clearHighlightKPI}
          />
        );
      case 'orders':
        if (ordersUnavailableMessage) return <OrdersComingSoon message={ordersUnavailableMessage} />;
        if (ordersSupport === 'loading') {
          return <p className="text-sm text-gray-400" data-testid="orders-availability-loading">Loading orders…</p>;
        }
        return (
          <OrdersSection
            data={useMockData ? MOCK_QARGO_DATA : qargoData}
            apiStatus={useMockData ? MOCK_DASHBOARD_OVERVIEW.apis.qargo : dashboardOverview?.apis?.qargo}
            hideNameColumn={
              (useMockData || orderIngestSources.includes('palletways'))
              && !orderIngestSources.includes('qargo')
            }
            // Always the truth-layer orders list, never the old static Qargo
            // export (/api/qargo/orders: June data, bucket no longer readable).
            // An empty or failed list shows as empty, not as a fall back to it.
            mockOrders={useMockData ? MOCK_ORDERS : (zeecoreOrderList as any)}
            onFetchData={async () => {
              // Read-only re-read of already-ingested truth-layer orders.
              // Source-API ingest (routine / deep / per-source) is admin-only via
              // the "Refresh All" control above, or the Data Import screen.
              await fetchFromZeecore(Math.max(7, alertsPeriodToDays(alertsPeriod)));
            }}
            isLoading={isLoading}
            highlightKPI={highlightKPI}
            initialPeriod={alertsPeriod}
            onHistoryWindowChange={handleOrdersHistoryWindowChange}
            orderSourceLabel={useMockData ? 'Palletways (demo)' : orderSourceLabel}
            orderSourceIds={useMockData ? ['palletways'] : orderIngestSources}
            ordersDailyEnabled={ordersDailyEnabled}
            demoMode={useMockData}
            onHighlightConsumed={clearHighlightKPI}
          />
        );
      case 'drivers':
        return (
          <DriverCompliance
            data={useMockData ? MOCK_AQUARIUS_DATA : aquariusData}
            apiStatus={useMockData ? MOCK_DASHBOARD_OVERVIEW.apis.aquarius : dashboardOverview?.apis?.aquarius}
            onFetchData={async () => {
              // Truth reload only — never legacy Python Aquarius export.
              // Vendor ingest (Aquarius + Samsara tachograph) is Refresh → Driver
              // in the Control Centre header, or Data Import → Tachograph.
              await fetchFromZeecore(Math.max(7, alertsPeriodToDays(alertsPeriod)));
            }}
            isLoading={isLoading}
            useMockData={useMockData}
            highlightKPI={highlightKPI}
            initialPeriod={highlightKPI ? alertsPeriod : undefined}
            driverSourceLabel={driverSourceLabel}
            driverSourceIds={driverIngestSources}
            onHighlightConsumed={clearHighlightKPI}
            refreshKey={driverTruth.refreshKey}
            onTruthFetchStart={driverTruth.markTruthFetch}
            onNavigateToMaintenance={(highlight) => {
              if (highlight) setHighlightKPI(highlight);
              setActiveSection('maintenance');
            }}
          />
        );
      case 'gps':
        return (
          <>
          <GPSTracking
            data={useMockData ? MOCK_OPTIFLEET_DATA : optifleetData}
            supatrakData={useMockData ? null : supatrakData}
            samsaraData={useMockData ? null : samsaraData}
            configuredProviders={configuredTelematicsProviders}
            apiStatus={useMockData ? MOCK_DASHBOARD_OVERVIEW.apis.optifleet : dashboardOverview?.apis?.optifleet}
            onFetchData={() => {
              fetchLiveTelematics({ showLoading: true });
            }}
            isLoading={isLoading}
            isBackgroundRefreshing={isTelematicsRefreshing}
            dataCurrentAsOf={liveTelematicsDataCurrentAsOf}
            loadingMessage={loadingMessage}
            useMockData={useMockData}
            highlightKPI={highlightKPI}
            onHighlightConsumed={clearHighlightKPI}
            onTelematicsTotals={setTelematicsTotals}
          />
          {!useMockData && configuredTelematicsProviders.includes('samsara') && <VehicleIdlePanel />}
          </>
        );
      case 'maintenance':
        return (
          <MaintenanceSection
            onFetchData={async () => {
              await fetchFromZeecore(Math.max(7, alertsPeriodToDays(alertsPeriod)));
            }}
            isLoading={isLoading}
            useMockData={useMockData}
            highlightKPI={highlightKPI}
            onHighlightConsumed={clearHighlightKPI}
            refreshKey={maintenanceIngestRefreshKey}
            initialPeriod={highlightKPI ? alertsPeriod : undefined}
          />
        );
      default:
        return null;
    }
  };

  // Count refresh progress (api sources + Maintenance when tracked this run)
  const refreshBannerSources = useMemo(() => {
    const sources: Array<{ id: string; name: string }> = [
      ...apiInfo.map((api) => ({ id: api.id, name: api.name })),
      { id: MAINTENANCE_REFRESH_SOURCE.id, name: MAINTENANCE_REFRESH_SOURCE.name },
    ];
    // Prefer chips that are part of the active refresh; fall back to full list.
    const active = sources.filter((s) => refreshStatus[s.id] != null);
    return active.length > 0 ? active : sources;
  }, [apiInfo, refreshStatus]);

  const refreshProgress = useMemo(() => {
    const total = refreshBannerSources.length;
    const completed = refreshBannerSources.filter((s) => refreshStatus[s.id] === 'complete').length;
    const loading = refreshBannerSources.filter((s) => refreshStatus[s.id] === 'loading').length;
    const errors = refreshBannerSources.filter((s) => refreshStatus[s.id] === 'error').length;
    return { total, completed, loading, errors };
  }, [refreshBannerSources, refreshStatus]);

  // Show loading state when fetching initial data (no cached data yet).
  // When Mock is on, always show main content with mock data (no loading screen).
  // Spinner is shown inside the content panel so the page header is not duplicated.
  const isInitialLoad = !useMockData && isLoading && !analyticsOverview && !error;

  if (view === 'system-health') {
    return isInitialLoad ? (
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 min-h-50">
        <div className="flex flex-col items-center justify-center py-24 text-gray-400">
          <RefreshCw className="w-12 h-12 animate-spin mb-4 text-blue-400" />
          <p className="text-lg font-medium">Loading fleet data...</p>
        </div>
      </div>
    ) : (
      <SystemHealthPage
        analyticsOverview={analyticsOverview}
        dashboardOverview={dashboardOverview}
        onFetchData={fetchFromZeecore}
        isLoading={isLoading}
        useMockData={false}
        jigsawData={jigsawData}
        optifleetData={optifleetData}
        aquariusData={aquariusData}
        orderStats={periodFilteredOrderStats ?? orderStats}
        samsaraData={samsaraData}
        telematicsTotals={telematicsTotals}
        driverStatsForAlerts={periodFilteredDriverStats}
        fuelStatsForAlerts={periodFilteredFuelStats}
        // Same deduped Orders-tab list (excludes note stubs / duplicate con_no) so
        // Key Insights + 7-day order health match the Orders tab completion %.
        ordersForHealthTrend={zeecoreOrderList.length > 0 ? zeecoreOrderList : ordersForAlerts}
        ordersDailyEnabled={ordersSupport === 'available'}
      />
    );
  }

  return (
    <div className="space-y-6 relative">
      {/* Non-blocking Refresh Banner */}
      {isRefreshing && (
        <div className="bg-ring/20 border border-ring/30 rounded-lg p-4 flex items-center gap-4">
          <div className="flex-shrink-0">
            <RefreshCw className="w-5 h-5 text-ring animate-spin" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="font-semibold text-white">Refreshing Fleet Data</h3>
              <span className="text-sm text-ring">
                {refreshProgress.completed}/{refreshProgress.total} complete
                {refreshProgress.loading > 0 && ` • ${refreshProgress.loading} syncing...`}
                {refreshProgress.errors > 0 && ` • ${refreshProgress.errors} failed`}
              </span>
            </div>
            {refreshDetailMessage && (
              <p className="text-xs text-ring/90 mb-2">{refreshDetailMessage}</p>
            )}
            <div className="flex gap-2 flex-wrap">
              {refreshBannerSources.map((source) => {
                const status = refreshStatus[source.id] || 'pending';
                const StatusIcon =
                  status === 'complete' ? CheckCircle :
                  status === 'loading' ? RefreshCw :
                  status === 'error' ? XCircle :
                  Clock;

                return (
                  <div
                    key={source.id}
                    className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs ${
                      status === 'loading'
                        ? 'bg-ring/20 text-ring'
                        : status === 'complete'
                        ? 'bg-success/20 text-success'
                        : status === 'error'
                        ? 'bg-destructive/20 text-destructive'
                        : 'bg-secondary/50 text-muted-foreground'
                    }`}
                  >
                    <StatusIcon className={`w-3 h-3 ${status === 'loading' ? 'animate-spin' : ''}`} />
                    <span>{source.name}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setIsRefreshing(false)}
            title="Minimize refresh status"
          >
            <XCircle className="w-5 h-5" aria-hidden />
          </Button>
        </div>
      )}

      {/* Header — single, persistent across loading and loaded states */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-11 h-11 rounded-lg bg-ring/10 border border-ring/30">
            <Database className="w-6 h-6 text-ring" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Fleet Control Centre</h1>
            <p className="text-sm text-muted-foreground">Live view across every connected fleet data source</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
          <div className="relative" ref={settingsRef}>
            <Button
              type="button"
              variant="secondary"
              size="md"
              onClick={() => {
                setShowRefreshDropdown(false);
                setShowSettings(!showSettings);
              }}
              className="border border-accent"
              title="Demo settings"
              aria-label="Demo settings"
              aria-expanded={showSettings}
            >
              <Settings className="w-5 h-5" aria-hidden />
            </Button>
            {showSettings && (
              <div className="absolute right-0 mt-2 w-64 bg-popover border border-accent rounded-lg shadow-xl z-50">
                <div className="p-4">
                  <h3 className="text-sm font-semibold text-muted-foreground mb-3">Demo</h3>
                  <label className="flex items-center justify-between cursor-pointer group">
                    <span className="text-sm text-muted-foreground group-hover:text-white transition-colors">Demo data</span>
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={useMockData}
                        onChange={(e) => setUseMockData(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-secondary peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-chart-5 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-empty after:absolute after:top-0.5 after:left-0.5 after:bg-white after:border-muted-foreground after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-chart-5"></div>
                    </div>
                  </label>
                  <p className="text-xs text-muted-foreground mt-2">
                    Show demo data across Control Centre (screenshots / walkthroughs). Live ingest is unchanged.
                  </p>
                </div>
              </div>
            )}
          </div>
          )}
          {isAdmin && (
          <div className="relative" ref={refreshDropdownRef}>
            <Button
              type="button"
              variant="default"
              size="sm"
              onClick={() => {
                setShowSettings(false);
                setShowRefreshDropdown(!showRefreshDropdown);
              }}
              disabled={isLoading || isRefreshing}
              aria-expanded={showRefreshDropdown}
              className="h-auto py-2 bg-ring text-white hover:bg-ring disabled:bg-ring/50 rounded-lg"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading || isRefreshing ? 'animate-spin' : ''}`} />
              Refresh All
              <ChevronDown className={`w-4 h-4 transition-transform ${showRefreshDropdown ? 'rotate-180' : ''}`} />
            </Button>

            {showRefreshDropdown && (
              <div className="absolute right-0 mt-2 w-72 bg-popover border border-border rounded-lg shadow-xl z-50 overflow-hidden">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    refreshLiveData();
                    setShowRefreshDropdown(false);
                  }}
                  disabled={isLoading || isRefreshing}
                  className="w-full h-auto px-4 py-3 flex-col items-start gap-0.5 text-left text-white border-b border-border hover:bg-secondary"
                >
                  <span className="flex items-center gap-2 font-medium">
                    <RefreshCw className="w-4 h-4" />
                    Refresh All
                  </span>
                  <span className="text-xs text-muted-foreground pl-6">Routine — ~2–3 day lookback</span>
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    refreshLiveData(undefined, { deepRefresh: true });
                    setShowRefreshDropdown(false);
                  }}
                  disabled={isLoading || isRefreshing}
                  className="w-full h-auto px-4 py-3 flex-col items-start gap-0.5 text-left text-white border-b border-border hover:bg-secondary"
                >
                  <span className="flex items-center gap-2 font-medium">
                    <RefreshCw className="w-4 h-4" />
                    Deep Refresh
                  </span>
                  <span className="text-xs text-muted-foreground pl-6">7 day catch-up — use when KPIs look stale</span>
                </Button>
                {apiInfo.map((api) => {
                  const status = refreshStatus[api.id];
                  const isRefreshingThis = status === 'loading';
                  const isComplete = status === 'complete';
                  const isError = status === 'error';

                  return (
                    <Button
                      key={api.id}
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        refreshLiveData(api.id);
                        setShowRefreshDropdown(false);
                      }}
                      disabled={isLoading || isRefreshing}
                      className="w-full h-auto px-4 py-3 justify-between hover:bg-secondary"
                    >
                      <div className="flex items-center gap-2">
                        {isRefreshingThis && <RefreshCw className="w-4 h-4 animate-spin text-ring" />}
                        {isComplete && !isRefreshingThis && <CheckCircle className="w-4 h-4 text-success" />}
                        {isError && !isRefreshingThis && <XCircle className="w-4 h-4 text-destructive" />}
                        {!isRefreshingThis && !isComplete && !isError && <Clock className="w-4 h-4 text-muted-foreground" />}
                        <span>{api.name}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">{api.description}</span>
                    </Button>
                  );
                })}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    refreshMaintenanceData();
                    setShowRefreshDropdown(false);
                  }}
                  disabled={isLoading || isRefreshing}
                  className="w-full h-auto px-4 py-3 justify-between hover:bg-secondary"
                >
                  <div className="flex items-center gap-2">
                    {refreshStatus.maintenance === 'loading' && (
                      <RefreshCw className="w-4 h-4 animate-spin text-ring" />
                    )}
                    {refreshStatus.maintenance === 'complete' && (
                      <CheckCircle className="w-4 h-4 text-success" />
                    )}
                    {refreshStatus.maintenance === 'error' && (
                      <XCircle className="w-4 h-4 text-destructive" />
                    )}
                    {refreshStatus.maintenance !== 'loading'
                      && refreshStatus.maintenance !== 'complete'
                      && refreshStatus.maintenance !== 'error' && (
                      <Clock className="w-4 h-4 text-muted-foreground" />
                    )}
                    <span>Maintenance</span>
                  </div>
                  <span className="text-xs text-muted-foreground">{MAINTENANCE_REFRESH_SOURCE.description}</span>
                </Button>
              </div>
            )}
          </div>
          )}
        </div>
      </div>

      {/* Section Navigation — sticky; z above DataTable sticky theads (z-20) */}
      <div
        ref={sectionNavRef}
        data-sticky-section-nav
        className="sticky top-0 z-40 bg-card rounded-lg border border-border p-1 flex gap-0 shadow-md shadow-black/30"
      >
        {sections.map((section, index) => {
          const Icon = section.icon;
          const isActive = activeSection === section.id;
          const isFirst = index === 0;
          const isLast = index === sections.length - 1;
          return (
            <Button
              key={section.id}
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => { setHighlightKPI(null); setActiveSection(section.id); }}
              className={`flex-1 justify-center h-auto px-4 py-2 ${
                isFirst ? 'rounded-l-md rounded-r-none' : isLast ? 'rounded-r-md rounded-l-none' : 'rounded-none'
              } ${
                isActive
                  ? 'bg-ring text-white'
                  : 'text-muted-foreground hover:text-white hover:bg-secondary'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span className="font-medium">{section.label}</span>
            </Button>
          );
        })}
      </div>

      {/* Section Content — overview uses its own section containers; other tabs share one panel */}
      {isInitialLoad ? (
        <div className="bg-card rounded-lg border border-border p-6 min-h-50">
          <div className="flex flex-col items-center justify-center py-24 text-muted-foreground">
            <RefreshCw className="w-12 h-12 animate-spin mb-4 text-ring" />
            <p className="text-lg font-medium">Loading fleet data...</p>
            <p className="text-sm mt-1">Fetching analytics from export files</p>
          </div>
        </div>
      ) : activeSection === 'overview' ? (
        renderSection()
      ) : (
        <div className="bg-card rounded-lg border border-border p-6 min-h-50">
          {renderSection()}
        </div>
      )}
    </div>
  );
}
